import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './Dashboard.css';
import dashboardAPI from '../api/dashboard';
import remindersAPI from '../api/reminders';
import dashboardHistoryAPI from '../api/dashboardHistory';
import authAPI, { apiRequest } from '../api/auth';
import { hasDashboardPermission } from '../utils/permissions';

// Funkcja pomocnicza do generowania danych karty (21 wierszy J01-J21)
const generateCardData = (baseSeq = 'C32033') => {
  const data = [];
  for (let i = 1; i <= 21; i++) {
    const jNumber = String(i).padStart(2, '0');
    data.push({
      check: '',
      seq: baseSeq,
      j: `J${jNumber}`,
      doZrobienia: null,
      zrobione: null,
      zainfekowane: null,
      komentarz: '',
      opis: ''
    });
  }
  return data;
};

// Check w modalu SEQ: liczba 1–10 lub pusty string (legacy: true/false z checkboxa → '1' / '')
const normalizeCheckField = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  if (v === true) return '1';
  if (v === false) return '';
  const n = parseInt(String(v).trim(), 10);
  if (Number.isNaN(n)) return '';
  if (n < 1) return '';
  if (n > 10) return '10';
  return String(n);
};

// Funkcja do uzyskania dzisiejszej daty w formacie YYYY-MM-DD
const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function Dashboard({ user, projects = [], users = [] }) {
  console.log('🏠 [Dashboard] Komponent Dashboard został zrenderowany, user:', user?.id, 'user object:', user);
  const saveTimeoutRef = useRef(null);

  // Pobierz pierwszy projekt użytkownika lub pierwszy dostępny projekt
  const getCurrentProject = () => {
    // Sprawdź uprawnienie "Wszystkie dane" - jeśli użytkownik ma to uprawnienie, widzi wszystkie projekty
    const canViewAll = hasDashboardPermission(user, 'viewAll');
    
    // Owner, Admin i użytkownicy z uprawnieniem viewAll widzą wszystkie projekty
    if (user?.role === 'owner' || user?.role === 'admin' || canViewAll) {
      if (projects && projects.length > 0) {
        return projects[0];
      }
      return null;
    }
    
    // Dla innych użytkowników - sprawdź przypisane projekty
    if (user?.projects && user.projects.length > 0) {
      return user.projects[0];
    }
    
    // Jeśli użytkownik nie ma przypisanych projektów, sprawdź czy są jakieś dostępne
    if (projects && projects.length > 0) {
      return projects[0];
    }
    
    return null;
  };

  // Dane - każda karta ma swoje własne dane (początkowo pusta tablica - użytkownik doda wiersze przez modal)
  const [cardsData, setCardsData] = useState(() => {
    const today = getTodayDate();
    return [
      {
        cardNumber: '01',
        trailerId: '',
        date: today,
        time: '17:00',
        code: 'PL',
        data: [] // Pusta tablica - użytkownik będzie mógł dodać wiersze przez modal
      },
      {
        cardNumber: '02',
        trailerId: '',
        date: today,
        time: '18:00',
        code: 'PA',
        data: [] // Pusta tablica - użytkownik będzie mógł dodać wiersze przez modal
      },
      {
        cardNumber: '03',
        trailerId: '',
        date: today,
        time: '19:00',
        code: 'TT',
        data: [] // Pusta tablica - użytkownik będzie mógł dodać wiersze przez modal
      }
    ];
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showProjectError, setShowProjectError] = useState(false);
  const [bulkSeqInput, setBulkSeqInput] = useState('');
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyModalTab, setHistoryModalTab] = useState('view'); // 'view' lub 'clear'
  const [selectedHistoryEntries, setSelectedHistoryEntries] = useState([]);
  const [historyClearMessage, setHistoryClearMessage] = useState(null); // Notyfikacja sukcesu/błędu w karcie Wyczyść
  const [historyTrailerSearch, setHistoryTrailerSearch] = useState(''); // Wyszukiwanie trailera w historii
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [activeReminderPopup, setActiveReminderPopup] = useState(null);
  const [reminderQueue, setReminderQueue] = useState([]);
  const [reminderModalTab, setReminderModalTab] = useState('add'); // 'add' lub 'delete'
  const [qualityAlertNotifications, setQualityAlertNotifications] = useState([]);
  const [activeQualityAlertPopup, setActiveQualityAlertPopup] = useState(null);
  const [selectedRemindersToDelete, setSelectedRemindersToDelete] = useState([]);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteConfirmData, setDeleteConfirmData] = useState({ count: 0, callback: null });
  const [dashboardHistory, setDashboardHistory] = useState([]);
  const [postponeMinutes, setPostponeMinutes] = useState(5);
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [statusConfirmData, setStatusConfirmData] = useState({ cardIndex: null, oldStatus: null, newStatus: null, callback: null });
  // Stan do śledzenia błędów walidacji dla każdej karty
  const [validationErrors, setValidationErrors] = useState({});
  // Stan dla trybu fullscreen
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Stan do śledzenia ostatniej aktywności użytkownika (timestamp)
  const lastActivityRef = useRef(Date.now());
  // Stan do śledzenia, które pola są w focus
  const focusedInputsRef = useRef(new Set());
  // Ref do przechowywania aktualnych danych kart (dla automatycznego odświeżania)
  const cardsDataRef = useRef(cardsData);
  const [reminderForm, setReminderForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    isRecurring: false,
    recurringDays: [], // Tablica dni tygodnia (0-6)
    recurringTime: '',
    assignedTo: 'all' // 'all' = do wszystkich (domyślnie), lub ID użytkownika
  });
  const [reminderSuccessMessage, setReminderSuccessMessage] = useState(null);
  const [draggedUser, setDraggedUser] = useState(null);
  const [draggedFromPosition, setDraggedFromPosition] = useState(null); // {cardIndex, rowIndex} - skąd został przeciągnięty przypisany użytkownik
  const [draggedFromComment, setDraggedFromComment] = useState(null); // {cardIndex, rowIndex, userName}
  const [selectedShiftInModal, setSelectedShiftInModal] = useState('A'); // Wybrana zmiana w modalu
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [isDraggingModal, setIsDraggingModal] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [selectedTool, setSelectedTool] = useState('menu'); // State dla wybranego narzędzia w toolbarze
  const [isToolbarVisible, setIsToolbarVisible] = useState(false); // State dla widoczności toolbara - domyślnie schowany
  const [selectedCell, setSelectedCell] = useState({ row: null, field: null }); // State dla zaznaczonej komórki w modalu Excel
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [dashboardZoom, setDashboardZoom] = useState(() => {
    const saved = localStorage.getItem('dashboardZoom');
    const parsed = Number(saved);
    if (!Number.isNaN(parsed) && parsed >= 50 && parsed <= 150) {
      return parsed;
    }
    return 100;
  });
  const [showExtendedColumns, setShowExtendedColumns] = useState(() => {
    // Załaduj stan z localStorage dla każdej karty osobno
    const saved = localStorage.getItem('dashboard_showExtendedColumns');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed; // Obiekt { '01': true, '02': false, '03': false }
      } catch (e) {
        return { '01': false, '02': false, '03': false };
      }
    }
    return { '01': false, '02': false, '03': false }; // Domyślnie wszystkie wyłączone
  }); // State dla przełącznika rozszerzonych kolumn (UP, UPok, AV, SK, ER) - osobno dla każdej karty
  
  // State dla pozycji ikony użytkownika
  const [userIconPosition, setUserIconPosition] = useState(() => {
    // Wczytaj pozycję z localStorage lub użyj domyślnej
    const saved = localStorage.getItem('userIconPosition');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return { x: 80, y: window.innerHeight - 70 };
      }
    }
    return { x: 80, y: window.innerHeight - 70 };
  });
  const [isDraggingUserIcon, setIsDraggingUserIcon] = useState(false);
  const [userIconDragOffset, setUserIconDragOffset] = useState({ x: 0, y: 0 });
  const hasMovedDuringDragRef = useRef(false);
  const userIconStartPositionRef = useRef({ x: 0, y: 0 });

  // Ładuj dane z API przy starcie i przy zmianie refreshKey
  useEffect(() => {
    const loadCards = async () => {
      // Jeśli użytkownik jeszcze się ładuje, nie rób nic
      if (!user) {
        return;
      }
      
      // Sprawdź uprawnienie "Przeglądanie" - jeśli użytkownik nie ma tego uprawnienia, nie ładuj danych
      if (!hasDashboardPermission(user, 'view')) {
        console.log('🚫 Użytkownik nie ma uprawnienia dashboard.view - nie ładuję danych');
        setLoading(false);
        setCardsData([]);
        return;
      }
      
      // Jeśli użytkownik nie jest zalogowany, zakończ ładowanie
      if (!authAPI.isAuthenticated()) {
        setLoading(false);
        return;
      }
      
      const project = getCurrentProject();
      if (!project) {
        // Jeśli użytkownik jest załadowany, ale brak projektu, zakończ ładowanie
        // (błąd zostanie wyświetlony w renderze)
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Ładuj wszystkie karty dla projektu (bez filtrowania po dacie)
        // Każda karta może mieć swoją datę
        const savedCards = await dashboardAPI.getByProject(project);
        
        const today = getTodayDate();
        const defaultCardNumbers = ['01', '02', '03'];
        
        // Jeśli są zapisane karty, użyj ich
        if (savedCards && savedCards.length > 0) {
          console.log('📥 Załadowane karty z API (SUROWE, wszystkie rekordy):', {
            count: savedCards.length,
            cards: savedCards.map(card => ({
              id: card.id,
              cardNumber: card.cardNumber,
              updatedAt: card.updatedAt,
              createdAt: card.createdAt,
              dataLength: card.data?.length || 0,
              dataIsArray: Array.isArray(card.data),
              allSeqs: card.data?.map(r => r.seq) || [],
              firstRowSeq: card.data?.[0]?.seq || 'BRAK',
              sampleData: card.data?.slice(0, 3).map(r => ({ seq: r.seq, j: r.j, opis: r.opis })) || []
            }))
          }); // Debug
          
          // Grupuj karty według numeru karty i wybierz najnowszą dla każdego numeru
          const cardsByNumber = {};
          savedCards.forEach(card => {
            const cardNum = card.cardNumber;
            if (!cardsByNumber[cardNum]) {
              cardsByNumber[cardNum] = [];
            }
            cardsByNumber[cardNum].push(card);
          });
          
          // Dla każdego numeru karty wybierz najnowszą (najnowsza data lub najnowszy updatedAt)
          const formattedCards = [];
          defaultCardNumbers.forEach(cardNum => {
            if (cardsByNumber[cardNum] && cardsByNumber[cardNum].length > 0) {
              // Sortuj karty dla tego numeru: najpierw po updatedAt (najnowsza pierwsza), potem po dacie
              // To zapewnia, że wyzerowana karta (z pustą datą ale najnowszym updatedAt) będzie wybrana
              const sortedCards = cardsByNumber[cardNum].sort((a, b) => {
                const updatedA = a.updatedAt || a.createdAt || '';
                const updatedB = b.updatedAt || b.createdAt || '';
                if (updatedA !== updatedB) {
                  return updatedB.localeCompare(updatedA); // Najnowszy updatedAt pierwsza
                }
                // Jeśli updatedAt jest takie samo, sortuj po dacie
                const dateA = a.date || '';
                const dateB = b.date || '';
                return dateB.localeCompare(dateA); // Najnowsza data pierwsza
              });
              
              // Weź najnowszą kartę
              const latestCard = sortedCards[0];
              
              console.log(`📋 Karta ${cardNum} - wybrana najnowsza:`, {
                id: latestCard.id,
                updatedAt: latestCard.updatedAt,
                createdAt: latestCard.createdAt,
                dataLength: latestCard.data?.length || 0,
                allSeqs: latestCard.data?.map(r => r.seq) || [],
                totalCardsForThisNumber: sortedCards.length,
                otherCards: sortedCards.slice(1).map(c => ({
                  id: c.id,
                  updatedAt: c.updatedAt,
                  dataLength: c.data?.length || 0
                }))
              }); // Debug
              
              // Loguj dane z bazy przed formatowaniem
              console.log(`📥 Karta ${cardNum} - dane z bazy (przed formatowaniem):`, {
                dataLength: latestCard.data?.length || 0,
                assignedToValues: latestCard.data?.map(r => ({ j: r.j, assignedTo: r.assignedTo, przypisanie: r.przypisanie })) || [],
                rawData: latestCard.data?.slice(0, 3) || []
              });
              
              formattedCards.push({
                id: latestCard.id,
                cardNumber: latestCard.cardNumber,
                // Zachowaj dokładnie to, co przyszło z backendu (nawet jeśli jest puste)
                trailerId: latestCard.trailerId !== undefined && latestCard.trailerId !== null ? latestCard.trailerId : '',
                date: latestCard.date !== undefined && latestCard.date !== null ? latestCard.date : '',
                time: latestCard.time !== undefined && latestCard.time !== null ? latestCard.time : '',
                code: latestCard.code !== undefined && latestCard.code !== null ? latestCard.code : 'PL',
                lastUpdateTime: latestCard.lastUpdateTime !== undefined && latestCard.lastUpdateTime !== null ? latestCard.lastUpdateTime : '',
                data: (latestCard.data && Array.isArray(latestCard.data))
                  ? latestCard.data.length > 0
                    ? latestCard.data.map(row => {
                        const formattedRow = {
                          ...row,
                          // WAŻNE: Zachowaj dokładnie to, co jest w bazie - nie generuj domyślnych wartości!
                          // Jeśli użytkownik wprowadził wartości, muszą być zachowane
                          check: normalizeCheckField(row.check),
                          seq: row.seq !== undefined && row.seq !== null ? row.seq : '', // Puste, jeśli nie ma w bazie
                          j: row.j !== undefined && row.j !== null ? row.j : '', // Puste, jeśli nie ma w bazie
                          opis: row.opis !== undefined && row.opis !== null ? (row.opis || row.description || '') : '', // Puste, jeśli nie ma w bazie
                          // WAŻNE: assignedTo - jeśli w bazie jest null, zachowaj null (nie używaj przypisanie jako fallback)
                          assignedTo: row.assignedTo !== undefined ? row.assignedTo : null, // null jeśli nie ma w bazie lub jest null
                          przypisanie: row.przypisanie !== undefined && row.przypisanie !== null ? row.przypisanie : (row.assignedTo !== undefined && row.assignedTo !== null ? row.assignedTo : null),
                          komentarz: row.komentarz !== undefined && row.komentarz !== null ? row.komentarz : null,
                          doZrobienia: row.doZrobienia !== undefined && row.doZrobienia !== null ? row.doZrobienia : null,
                          zrobione: row.zrobione !== undefined && row.zrobione !== null ? row.zrobione : null,
                          zainfekowane: row.zainfekowane !== undefined && row.zainfekowane !== null ? row.zainfekowane : null,
                          ocz: row.ocz !== undefined && row.ocz !== null ? row.ocz : '',
                          up: row.up !== undefined && row.up !== null ? row.up : '',
                          upok: row.upok !== undefined && row.upok !== null ? row.upok : '',
                          av: row.av !== undefined && row.av !== null ? row.av : '',
                          sk: row.sk !== undefined && row.sk !== null ? row.sk : '',
                          er: row.er !== undefined && row.er !== null ? row.er : ''
                        };
                        return formattedRow;
                      })
                    : [] // Pusta tablica - użytkownik usunął wszystkie wiersze
                  : (latestCard.data === null || latestCard.data === undefined)
                    ? [] // null/undefined w bazie = pusta tablica (użytkownik usunął wszystkie wiersze lub karta jest nowa)
                    : [] // Fallback - jeśli data nie jest tablicą, użyj pustej tablicy
              });
              
              // Loguj dane po formatowaniu
              const formattedCard = formattedCards[formattedCards.length - 1];
              console.log(`📥 Karta ${cardNum} - dane po formatowaniu:`, {
                dataLength: formattedCard.data?.length || 0,
                assignedToValues: formattedCard.data?.map(r => ({ j: r.j, assignedTo: r.assignedTo })) || []
              });
            } else {
              // Brak karty dla tego numeru - użyj domyślnej z pustą tablicą danych
              // (użytkownik będzie mógł dodać wiersze przez modal)
              formattedCards.push({
                cardNumber: cardNum,
                trailerId: '',
                date: today,
                time: cardNum === '01' ? '17:00' : cardNum === '02' ? '18:00' : '19:00',
                code: cardNum === '01' ? 'PL' : cardNum === '02' ? 'PA' : 'TT',
                lastUpdateTime: '', // Pusty czas ostatniej aktualizacji dla nowej karty
                data: [] // Pusta tablica - użytkownik będzie mógł dodać wiersze przez modal
              });
            }
          });
          
          console.log('Sformatowane karty (najnowsze dla każdego numeru):', formattedCards.map(card => ({
            cardNumber: card.cardNumber,
            dataLength: card.data?.length || 0,
            allSeqs: card.data?.map(r => r.seq) || []
          }))); // Debug
          
          // Sortuj według numeru karty
          formattedCards.sort((a, b) => a.cardNumber.localeCompare(b.cardNumber));
          
          console.log('📋 Karty PRZED synchronizacją (dane z bazy):', formattedCards.map(card => ({
            cardNumber: card.cardNumber,
            dataLength: card.data?.length || 0,
            allSeqs: card.data?.map(r => r.seq) || [],
            firstRowSeq: card.data?.[0]?.seq || 'BRAK'
          }))); // Debug
          
          // WAŻNE: Synchronizuj wiersze przy ładowaniu z bazy, używając pierwszej karty (L01) jako wzorca
          // Jeśli L01 jest puste, wszystkie karty powinny być puste
          // Jeśli L01 ma dane, wszystkie karty powinny mieć te same dane (J, SEQ, Opis)
          // ALE: Jeśli w L01 są puste wartości w J/SEQ/Opis, a w innych kartach są wartości, zachowaj wartości z innych kart
          // To zapewnia, że dane są spójne między wszystkimi kartami, ale nie nadpisujemy wartości użytkownika
          const synchronizedCards = synchronizeRows(formattedCards);
          
          console.log('📋 Karty PO synchronizacji (pierwsza karta jako wzorzec):', synchronizedCards.map(card => ({
            cardNumber: card.cardNumber,
            dataLength: card.data?.length || 0,
            allSeqs: card.data?.map(r => r.seq) || [],
            allJs: card.data?.map(r => r.j) || [],
            allOpis: card.data?.map(r => r.opis) || [],
            firstRowSeq: card.data?.[0]?.seq || 'BRAK'
          }))); // Debug
          
          // WAŻNE: Zapisz zsynchronizowane karty do bazy, aby usunąć stare dane
          // (jeśli L01 było puste, a L03 miało stare dane, teraz L03 też będzie puste)
          // ALE: Nie zapisuj natychmiast, aby nie nadpisać wartości użytkownika, które mogą być jeszcze edytowane
          // Zapis nastąpi automatycznie przez debouncing przy następnej zmianie
          
          setCardsData(synchronizedCards);
          setLoading(false); // Zakończ ładowanie PRZED zapisywaniem brakujących kart
          
          // Upewnij się, że wszystkie 3 karty są zapisane w bazie
          // Jeśli brakuje którejś karty, zapisz ją automatycznie (asynchronicznie, bez blokowania UI)
          const existingCardNumbers = synchronizedCards.map(c => c.cardNumber);
          const missingCards = defaultCardNumbers.filter(cardNum => !existingCardNumbers.includes(cardNum));
          
          if (missingCards.length > 0) {
            console.log('📝 Brakujące karty w bazie, zapisuję automatycznie (w tle):', missingCards);
            // Zapisz asynchronicznie w tle, bez blokowania UI
            setTimeout(() => {
              missingCards.forEach(cardNum => {
                const defaultCard = {
                  cardNumber: cardNum,
                  trailerId: '',
                  date: today,
                  time: cardNum === '01' ? '17:00' : cardNum === '02' ? '18:00' : '19:00',
                  code: cardNum === '01' ? 'PL' : cardNum === '02' ? 'PA' : 'TT',
                  data: [] // Pusta tablica - użytkownik będzie mógł dodać wiersze przez modal
                };
                saveCard(defaultCard, defaultCardNumbers.indexOf(cardNum));
              });
            }, 100); // Krótkie opóźnienie, aby UI się najpierw wyświetliło
          }
        } else {
          // Jeśli nie ma żadnych zapisanych kart, użyj domyślnych z pustą tablicą danych
          // (użytkownik będzie mógł dodać wiersze przez modal)
          const defaultCards = defaultCardNumbers.map(cardNum => ({
            cardNumber: cardNum,
            trailerId: '',
            date: today,
            time: cardNum === '01' ? '17:00' : cardNum === '02' ? '18:00' : '19:00',
            code: cardNum === '01' ? 'PL' : cardNum === '02' ? 'PA' : 'TT',
            data: [] // Pusta tablica - użytkownik będzie mógł dodać wiersze przez modal
          }));
          
          // Synchronizuj liczbę wierszy PRZED ustawieniem danych
          const synchronizedDefaultCards = synchronizeRows(defaultCards);
          setCardsData(synchronizedDefaultCards);
          setLoading(false); // Zakończ ładowanie PRZED zapisywaniem kart
          
          // Zapisz wszystkie karty do bazy przy pierwszym załadowaniu (asynchronicznie, bez blokowania UI)
          console.log('📝 Pierwsze załadowanie - zapisuję wszystkie 3 karty do bazy (w tle)');
          setTimeout(() => {
            synchronizedDefaultCards.forEach((card, index) => {
              saveCard(card, index);
            });
          }, 100); // Krótkie opóźnienie, aby UI się najpierw wyświetliło
        }
      } catch (error) {
        console.error('Błąd ładowania kart Dashboard:', error);
        setLoading(false); // Zakończ ładowanie nawet w przypadku błędu
        // W przypadku błędu, użyj domyślnych kart
        const today = getTodayDate();
        const defaultCards = ['01', '02', '03'].map(cardNum => ({
          cardNumber: cardNum,
          trailerId: '',
          date: today,
          time: cardNum === '01' ? '17:00' : cardNum === '02' ? '18:00' : '19:00',
          code: cardNum === '01' ? 'PL' : cardNum === '02' ? 'PA' : 'TT',
          data: [] // Pusta tablica - użytkownik będzie mógł dodać wiersze przez modal
        }));
        
        // Synchronizuj liczbę wierszy
        const synchronizedErrorCards = synchronizeRows(defaultCards);
        setCardsData(synchronizedErrorCards);
      } finally {
        setLoading(false);
      }
    };

    loadCards();
  }, [user, projects, refreshKey]);

  // Nasłuchuj na eventy nawigacji do dashboard, aby odświeżyć dane
  useEffect(() => {
    const handleNavigate = (event) => {
      if (event.detail === 'dashboard') {
        // Odśwież dane przy przełączeniu na dashboard
        console.log('Odświeżanie danych Dashboard po przełączeniu widoku');
        setRefreshKey(prev => prev + 1);
      }
    };

    window.addEventListener('navigate', handleNavigate);
    return () => window.removeEventListener('navigate', handleNavigate);
  }, []);

  // Załaduj przypomnienia z API (współdzielone w projekcie)
  useEffect(() => {
    const loadReminders = async () => {
      const project = getCurrentProject();
      if (!project || !authAPI.isAuthenticated()) return;
      
      try {
        // API automatycznie filtruje przypomnienia (dla wszystkich lub dla zalogowanego użytkownika)
        const loadedReminders = await remindersAPI.getByProject(project);
        setReminders(loadedReminders || []);
      } catch (error) {
        console.error('Błąd ładowania przypomnień:', error);
        setReminders([]);
      }
    };
    
    // Załaduj od razu
    loadReminders();
    
    // Automatycznie odświeżaj przypomnienia co 30 sekund, aby widzieć nowe przypomnienia dodane przez innych użytkowników
    const refreshInterval = setInterval(loadReminders, 30000);
    
    return () => clearInterval(refreshInterval);
  }, [user, projects]);


  // Załaduj historię Dashboard z API (współdzielone w projekcie)
  // Funkcja jest dostępna globalnie w komponencie, aby można było ją wywołać z innych miejsc
  const loadDashboardHistory = useCallback(async () => {
    // Sprawdź uprawnienie "Przeglądanie historii" - jeśli użytkownik nie ma tego uprawnienia, nie ładuj historii
    if (!hasDashboardPermission(user, 'viewHistory')) {
      console.log('🚫 Użytkownik nie ma uprawnienia dashboard.viewHistory - nie ładuję historii');
      setDashboardHistory([]);
      return;
    }
    
    const project = getCurrentProject();
    if (!project || !authAPI.isAuthenticated()) return;
    
    try {
      const loadedHistory = await dashboardHistoryAPI.getByProject(project);
      setDashboardHistory(loadedHistory || []);
    } catch (error) {
      console.error('Błąd ładowania historii Dashboard:', error);
      setDashboardHistory([]);
    }
  }, [user, projects]);

  useEffect(() => {
    // Załaduj od razu
    loadDashboardHistory();
    
    // Automatycznie odświeżaj historię co 10 sekund (zamiast 30), aby szybciej widzieć zmiany z innych kont
    const refreshInterval = setInterval(loadDashboardHistory, 10000);
    
    return () => clearInterval(refreshInterval);
  }, [loadDashboardHistory]);

  // Sprawdzaj przypomnienia co minutę
  useEffect(() => {
    const checkReminders = () => {
      const now = new Date();
      const currentDate = getTodayDate(); // Użyj funkcji getTodayDate() dla spójności
      const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const currentDay = now.getDay(); // 0 = niedziela, 1 = poniedziałek, etc.
      const nowTimestamp = now.getTime();
      const toTimestamp = (dateStr, timeStr) => {
        if (!dateStr || !timeStr) return null;
        return new Date(`${dateStr}T${timeStr}`).getTime();
      };
      const enqueueReminder = (reminder, lastShownKey) => {
        if (localStorage.getItem(lastShownKey)) return;
        localStorage.setItem(lastShownKey, 'true');
        setReminderQueue(prev => {
          if (activeReminderPopup?.id === reminder.id || prev.some(r => r.id === reminder.id)) {
            return prev;
          }
          return [...prev, reminder];
        });
      };

      reminders.forEach(reminder => {
        // Sprawdź czy przypomnienie jest dla tego użytkownika (lub dla wszystkich)
        // assignedTo === null lub undefined = dla wszystkich
        // assignedTo === user.id = dla konkretnego użytkownika
        if (reminder.assignedTo !== null && reminder.assignedTo !== undefined && reminder.assignedTo !== user?.id) {
          return; // Pomiń przypomnienia przypisane do innych użytkowników
        }
        
        // Sprawdź czy przypomnienie zostało odłożone
        if (reminder.postponedUntil) {
          const postponedTimestamp = toTimestamp(currentDate, reminder.postponedUntil);
          if (postponedTimestamp !== null && nowTimestamp >= postponedTimestamp) {
            const lastShownKey = `reminder_${reminder.id}_${currentDate}_postponed_${reminder.postponedUntil}`;
            enqueueReminder(reminder, lastShownKey);
          }
          return; // Nie sprawdzaj normalnego czasu jeśli jest odłożone
        }

        if (reminder.isRecurring) {
          // Przypomnienie powtarzające się
          const reminderDays = reminder.recurringDays || (reminder.recurringDay !== undefined ? [parseInt(reminder.recurringDay)] : []);
          // Obsługa starego formatu (pojedynczy dzień) i nowego (tablica dni)
          const recurringTimestamp = toTimestamp(currentDate, reminder.recurringTime);
          if (reminderDays.includes(currentDay) && recurringTimestamp !== null && nowTimestamp >= recurringTimestamp) {
            // Sprawdź czy już nie było wyświetlone dzisiaj dla tej godziny
            const lastShownKey = `reminder_${reminder.id}_${currentDate}_recurring_${reminder.recurringTime}`;
            enqueueReminder(reminder, lastShownKey);
          }
        } else {
          // Przypomnienie jednorazowe
          const reminderTimestamp = toTimestamp(reminder.date, reminder.time);
          if (reminder.date === currentDate && reminderTimestamp !== null && nowTimestamp >= reminderTimestamp) {
            // Sprawdź czy już nie było wyświetlone dzisiaj
            const lastShownKey = `reminder_${reminder.id}_${currentDate}_once`;
            enqueueReminder(reminder, lastShownKey);
          }
        }
      });
    };

    // Sprawdzaj co 10 sekund (zamiast co minutę) - szybsza reakcja na przypomnienia
    const interval = setInterval(checkReminders, 10000);
    // Sprawdź od razu przy załadowaniu
    checkReminders();

    return () => clearInterval(interval);
  }, [reminders, user, activeReminderPopup]); // Dodaj user do dependencies, aby filtrowanie działało poprawnie

  // Pokazuj przypomnienia jedno po drugim
  useEffect(() => {
    if (!activeReminderPopup && reminderQueue.length > 0) {
      setActiveReminderPopup(reminderQueue[0]);
      setReminderQueue(prev => prev.slice(1));
    }
  }, [activeReminderPopup, reminderQueue]);

  // Załaduj powiadomienia o alertach jakościowych – filtrowanie "due" po stronie klienta (jak Przypomnienia: data/godzina w strefie użytkownika)
  useEffect(() => {
    if (!user?.id || !authAPI.isAuthenticated()) return;

    const loadQualityAlertNotifications = async () => {
      try {
        const list = await apiRequest('/quality/notifications/unread');
        const all = Array.isArray(list) ? list : [];
        const now = new Date();
        const due = all.filter((n) => {
          if (!n.scheduledAt) return true;
          const raw = String(n.scheduledAt).trim();
          const str = raw.replace(' ', 'T').slice(0, 19);
          const scheduled = new Date(str);
          if (isNaN(scheduled.getTime())) return true;
          return scheduled <= now;
        });
        setQualityAlertNotifications(due);
        setActiveQualityAlertPopup(prev => {
          if (prev) return prev;
          return due.length > 0 ? due[0] : null;
        });
      } catch (e) {
        console.warn('Powiadomienia jakościowe (unread):', e?.message || e);
        setQualityAlertNotifications([]);
      }
    };

    loadQualityAlertNotifications();
    const refreshInterval = setInterval(loadQualityAlertNotifications, 15000);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') loadQualityAlertNotifications();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user?.id]);

  // Po utworzeniu nowego alertu jakości odśwież powiadomienia (z tym samym filtrem "due" po stronie klienta)
  useEffect(() => {
    if (!user?.id || !authAPI.isAuthenticated()) return;
    const onQualityAlertCreated = () => {
      apiRequest('/quality/notifications/unread')
        .then((list) => {
          const all = Array.isArray(list) ? list : [];
          const now = new Date();
          const due = all.filter((n) => {
            if (!n.scheduledAt) return true;
            const raw = String(n.scheduledAt).trim();
            const str = raw.replace(' ', 'T').slice(0, 19);
            const scheduled = new Date(str);
            if (isNaN(scheduled.getTime())) return true;
            return scheduled <= now;
          });
          setQualityAlertNotifications(due);
          setActiveQualityAlertPopup(prev => prev || (due.length > 0 ? due[0] : null));
        })
        .catch(() => {});
    };
    window.addEventListener('quality-alert-created', onQualityAlertCreated);
    return () => window.removeEventListener('quality-alert-created', onQualityAlertCreated);
  }, [user?.id]);

  // Pokazuj pierwsze powiadomienie jakościowe z listy, jeśli nie ma aktywnego
  useEffect(() => {
    if (activeQualityAlertPopup || qualityAlertNotifications.length === 0) return;
    setActiveQualityAlertPopup(qualityAlertNotifications[0]);
  }, [qualityAlertNotifications, activeQualityAlertPopup]);

  // Funkcja do zapisywania karty z debouncing (dla normalnych edycji)
  const saveCard = async (card, index, immediate = false) => {
    const project = getCurrentProject();
    if (!project || !authAPI.isAuthenticated()) {
      return;
    }

    // Anuluj poprzedni timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    const saveFunction = async () => {
      try {
        setSaving(true);
        const project = getCurrentProject();
        if (!project) return;
        
        const cardToSave = {
          project: project,
          cardNumber: card.cardNumber,
          trailerId: card.trailerId !== null && card.trailerId !== undefined ? card.trailerId : '',
          date: card.date !== null && card.date !== undefined ? card.date : '',
          time: card.time !== null && card.time !== undefined ? card.time : '',
          code: card.code !== null && card.code !== undefined ? card.code : 'PL',
          lastUpdateTime: card.lastUpdateTime !== null && card.lastUpdateTime !== undefined ? card.lastUpdateTime : '',
          data: Array.isArray(card.data) ? card.data : [] // Upewnij się, że data jest tablicą
        };
        
            console.log('💾 Zapisywanie karty:', {
              cardNumber: cardToSave.cardNumber,
              project: cardToSave.project,
              dataLength: cardToSave.data?.length || 0,
              dataIsArray: Array.isArray(cardToSave.data),
              firstRowSeq: cardToSave.data?.[0]?.seq || 'BRAK',
              allSeqs: cardToSave.data?.map(r => r.seq) || [],
              allJs: cardToSave.data?.map(r => r.j) || [],
              allOpis: cardToSave.data?.map(r => r.opis) || [],
              assignedToValues: cardToSave.data?.map(r => ({ j: r.j, assignedTo: r.assignedTo })) || [],
              sampleData: cardToSave.data?.slice(0, 5).map(r => ({ 
                seq: r.seq, 
                j: r.j, 
                opis: r.opis,
                assignedTo: r.assignedTo 
              })) || []
            }); // Debug
        const savedCard = await dashboardAPI.upsert(cardToSave);
        console.log('✅ Karta zapisana pomyślnie:', {
          id: savedCard?.id,
          cardNumber: savedCard?.cardNumber,
          dataLength: savedCard?.data?.length || 0,
          firstRowSeq: savedCard?.data?.[0]?.seq || 'BRAK',
          allSeqs: savedCard?.data?.map(r => r.seq) || [],
          allJs: savedCard?.data?.map(r => r.j) || [],
          allOpis: savedCard?.data?.map(r => r.opis) || [],
          assignedToValues: savedCard?.data?.map(r => ({ j: r.j, assignedTo: r.assignedTo })) || [],
          sampleData: savedCard?.data?.slice(0, 5).map(r => ({ 
            seq: r.seq, 
            j: r.j, 
            opis: r.opis,
            assignedTo: r.assignedTo 
          })) || []
        }); // Debug
        return savedCard; // Zwróć zapisaną kartę
      } catch (error) {
        console.error('Błąd zapisywania karty Dashboard:', error);
        throw error; // Rzuć błąd, aby Promise.all mógł go obsłużyć
      } finally {
        setSaving(false);
      }
    };

    if (immediate) {
      // Zapisz natychmiast (bez debouncing) - dla operacji usuwania wierszy
      return await saveFunction();
    } else {
      // Ustaw timeout (zapis po 1 sekundzie bez zmian) - dla normalnych edycji
      saveTimeoutRef.current = setTimeout(saveFunction, 1000);
      return Promise.resolve(); // Zwróć resolved promise dla consistency
    }
  };

  const handleTrailerIdChange = (index, value) => {
    // Sprawdź uprawnienie do edycji Dashboard
    if (!hasDashboardPermission(user, 'edit')) {
      alert('Nie masz uprawnień do edycji Dashboard.');
      return;
    }
    
    const updatedCards = [...cardsData];
    updatedCards[index].trailerId = value;
    setCardsData(updatedCards);
    saveCard(updatedCards[index], index);
    
    // Wyczyść błąd walidacji dla tego pola, jeśli został wypełniony
    if (value && value.trim() !== '') {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors[index]) {
          delete newErrors[index].trailerId;
          if (Object.keys(newErrors[index]).length === 0) {
            delete newErrors[index];
          }
        }
        return newErrors;
      });
    }
  };

  const handleTimeChange = (index, value) => {
    const updatedCards = [...cardsData];
    updatedCards[index].time = value;
    setCardsData(updatedCards);
    saveCard(updatedCards[index], index);
    
    // Wyczyść błąd walidacji dla tego pola, jeśli został wypełniony
    if (value && value.trim() !== '') {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors[index]) {
          delete newErrors[index].time;
          if (Object.keys(newErrors[index]).length === 0) {
            delete newErrors[index];
          }
        }
        return newErrors;
      });
    }
  };

  const handleStatusChange = (index, value) => {
    // Sprawdź uprawnienie do edycji Dashboard
    if (!hasDashboardPermission(user, 'edit')) {
      alert('Nie masz uprawnień do zmiany statusu kart.');
      return;
    }
    
    const currentCard = cardsData[index];
    const oldStatus = currentCard.code;
    const newStatus = value;

    // Jeśli status się nie zmienił, nie rób nic
    if (oldStatus === newStatus) {
      return;
    }

    // Walidacja przed zmianą statusu z PL na PA
    if (oldStatus === 'PL' && newStatus === 'PA') {
      const errors = {};
      let hasErrors = false;

      // Sprawdź, czy pola Date, Time, Trailer są wypełnione
      if (!currentCard.date || currentCard.date.trim() === '') {
        errors.date = true;
        hasErrors = true;
      }
      
      if (!currentCard.time || currentCard.time.trim() === '') {
        errors.time = true;
        hasErrors = true;
      }
      
      if (!currentCard.trailerId || currentCard.trailerId.trim() === '') {
        errors.trailerId = true;
        hasErrors = true;
      }
      
      // Sprawdź, czy co najmniej jedno pole w kolumnie "Ilość" (doZrobienia) jest wypełnione
      // Uwaga: 0 jest ważną wartością, więc sprawdzamy tylko null/undefined/pusty string
      const hasAnyQuantity = currentCard.data && currentCard.data.length > 0 && currentCard.data.some(row => {
        const value = row.doZrobienia;
        // Sprawdź, czy wartość jest wypełniona (nie null, nie undefined, nie pusty string)
        // Liczba 0 jest ważną wartością, więc ją akceptujemy
        return value !== null && value !== undefined && value !== '';
      });
      
      if (!hasAnyQuantity) {
        errors.quantity = true;
        hasErrors = true;
      }

      // Jeśli są błędy, ustaw je i nie pozwól na zmianę statusu
      if (hasErrors) {
        setValidationErrors(prev => ({
          ...prev,
          [index]: errors
        }));
        return;
      }

      // Wyczyść błędy dla tej karty, jeśli walidacja przeszła
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    } else {
      // Wyczyść błędy dla tej karty, jeśli nie zmieniamy z PL na PA
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }

    // Pokaż modal potwierdzający
    setStatusConfirmData({
      cardIndex: index,
      oldStatus: oldStatus,
      newStatus: newStatus,
      callback: () => {
        performStatusChange(index, oldStatus, newStatus);
      }
    });
    setShowStatusConfirmModal(true);
  };

  const performStatusChange = async (index, oldStatus, newStatus) => {
    const currentCard = cardsData[index];
    const project = getCurrentProject();
    if (!project) return;

    const now = new Date();
    const dateStr = now.toLocaleDateString('pl-PL');
    const timeStr = now.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });

    // Jeśli przełączamy z PL na PA - dodaj nowy wpis do historii przez API
    if (oldStatus === 'PL' && newStatus === 'PA') {
      try {
        const userName = user?.name || user?.firstName || 'Nieznany';
        const userLastName = user?.lastName || '';
        
        console.log('📝 Próba zapisania historii Dashboard (PL->PA):', {
          project,
          cardNumber: `L${currentCard.cardNumber}`,
          date: dateStr,
          time: timeStr,
          userName,
          userLastName,
          statusFrom: oldStatus,
          statusTo: newStatus
        });
        
        const historyEntry = await dashboardHistoryAPI.create({
          project,
          cardNumber: `L${currentCard.cardNumber}`,
          date: dateStr,
          time: timeStr,
          dateValue: currentCard.date || '',
          timeValue: currentCard.time || '',
          trailerId: currentCard.trailerId || '',
          statusFrom: oldStatus,
          statusTo: newStatus,
          statusToDate: null,
          statusToTime: null,
          userName,
          userLastName
        });

        console.log('✅ Historia Dashboard zapisana:', historyEntry);

        // Odśwież historię z API (dla użytkownika, który dokonał zmiany)
        const updatedHistory = await dashboardHistoryAPI.getByProject(project);
        console.log('✅ Historia Dashboard odświeżona:', updatedHistory?.length || 0, 'wpisów');
        setDashboardHistory(updatedHistory || []);
        
        // Dodatkowo odśwież po 1 sekundzie, aby upewnić się, że zmiany są widoczne
        // (to pomaga w przypadku, gdy inne użytkownicy również dodali wpisy w tym samym czasie)
        setTimeout(() => {
          loadDashboardHistory();
        }, 1000);
      } catch (error) {
        console.error('❌ Błąd zapisywania historii Dashboard (PL->PA):', error);
        console.error('❌ Szczegóły błędu:', {
          message: error.message,
          stack: error.stack,
          response: error.response
        });
        alert(`Błąd zapisywania historii Dashboard: ${error.message || error}`);
      }
    }

    // Jeśli przełączamy z PA na TT - utwórz nowy wpis PA → TT (nie aktualizuj istniejącego PL → PA)
    if (oldStatus === 'PA' && newStatus === 'TT') {
      try {
        const userName = user?.name || user?.firstName || 'Nieznany';
        const userLastName = user?.lastName || '';
        
        console.log('📝 Próba zapisania historii Dashboard (PA->TT):', {
          project,
          cardNumber: `L${currentCard.cardNumber}`,
          date: dateStr,
          time: timeStr,
          userName,
          userLastName,
          statusFrom: 'PA',
          statusTo: 'TT'
        });
        
        // Zawsze utwórz nowy wpis PA → TT (nie aktualizuj istniejącego PL → PA)
        const historyEntry = await dashboardHistoryAPI.create({
          project,
          cardNumber: `L${currentCard.cardNumber}`,
          date: dateStr,
          time: timeStr,
          dateValue: currentCard.date || '',
          timeValue: currentCard.time || '',
          trailerId: currentCard.trailerId || '',
          statusFrom: 'PA',
          statusTo: 'TT',
          statusToDate: dateStr,
          statusToTime: timeStr,
          userName,
          userLastName
        });

        console.log('✅ Historia Dashboard zapisana (PA->TT):', historyEntry);

        // Odśwież historię z API (dla użytkownika, który dokonał zmiany)
        const updatedHistory = await dashboardHistoryAPI.getByProject(project);
        console.log('✅ Historia Dashboard odświeżona (PA->TT):', updatedHistory?.length || 0, 'wpisów');
        setDashboardHistory(updatedHistory || []);
        
        // Dodatkowo odśwież po 1 sekundzie, aby upewnić się, że zmiany są widoczne
        setTimeout(() => {
          loadDashboardHistory();
        }, 1000);
      } catch (error) {
        console.error('Błąd zapisywania historii Dashboard (PA->TT):', error);
      }

      // Wyzeruj kartę i ustaw status na PL (punkt startowy)
      const updatedCards = cardsData.map((card, idx) => {
        if (idx === index) {
          return {
            ...card,
            date: '',
            time: '',
            trailerId: '',
            code: 'PL', // Wróć do statusu PL (punkt startowy)
            lastUpdateTime: '', // Wyczyść czas ostatniej aktualizacji
            data: card.data.map(row => ({
              check: normalizeCheckField(row.check),
              seq: row.seq,
              j: row.j,
              opis: row.opis || '',
              doZrobienia: null,
              zrobione: null,
              przypisanie: null,
              assignedTo: null,
              komentarz: null,
              ocz: '',
              up: '',
              upok: '',
              av: '',
              sk: '',
              er: ''
            }))
          };
        }
        return card;
      });

      setCardsData(updatedCards);
      
      // Zapisz do backendu (project jest już zdefiniowane na początku funkcji)
      if (project && authAPI.isAuthenticated()) {
        const cardToSave = {
          project: project,
          cardNumber: updatedCards[index].cardNumber,
          trailerId: '',
          date: '',
          time: '',
          code: 'PL', // Wróć do statusu PL (punkt startowy)
          lastUpdateTime: '', // Wyczyść czas ostatniej aktualizacji
          data: updatedCards[index].data.map(row => ({
            check: normalizeCheckField(row.check),
            seq: row.seq,
            j: row.j,
            opis: row.opis || '',
            doZrobienia: null,
            zrobione: null,
            przypisanie: null,
            assignedTo: null,
            komentarz: null
          }))
        };
        
        dashboardAPI.upsert(cardToSave).catch(error => {
          console.error('Błąd zapisywania wyzerowanej karty:', error);
        });
      }
      
      return; // Karta już zapisana
    }

    // Dla innych zmian statusu po prostu zaktualizuj
    const updatedCards = [...cardsData];
    updatedCards[index].code = newStatus;
    setCardsData(updatedCards);
    saveCard(updatedCards[index], index);
  };

  const handleDateChange = (index, value) => {
    // Sprawdź uprawnienie do edycji Dashboard
    if (!hasDashboardPermission(user, 'edit')) {
      alert('Nie masz uprawnień do edycji Dashboard.');
      return;
    }
    
    const updatedCards = [...cardsData];
    updatedCards[index].date = value;
    setCardsData(updatedCards);
    saveCard(updatedCards[index], index);
    
    // Wyczyść błąd walidacji dla tego pola, jeśli został wypełniony
    if (value && value.trim() !== '') {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors[index]) {
          delete newErrors[index].date;
          if (Object.keys(newErrors[index]).length === 0) {
            delete newErrors[index];
          }
        }
        return newErrors;
      });
    }
  };

  // Funkcja do przełączania trybu fullscreen
  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        // Wejdź w tryb fullscreen
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        // Wyjdź z trybu fullscreen
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Błąd przełączania fullscreen:', error);
    }
  };

  // Nasłuchuj zmian trybu fullscreen (np. ESC)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreenActive = !!document.fullscreenElement;
      setIsFullscreen(isFullscreenActive);
      
      // Dodaj/usuń klasę na body i html, aby ukryć navbar
      if (isFullscreenActive) {
        document.body.classList.add('fullscreen-active');
        document.documentElement.classList.add('fullscreen-active');
        // Dodaj również do elementu App, jeśli istnieje
        const appElement = document.querySelector('.App');
        if (appElement) {
          appElement.classList.add('fullscreen-active');
        }
      } else {
        document.body.classList.remove('fullscreen-active');
        document.documentElement.classList.remove('fullscreen-active');
        const appElement = document.querySelector('.App');
        if (appElement) {
          appElement.classList.remove('fullscreen-active');
        }
      }
    };

    // Sprawdź początkowy stan
    handleFullscreenChange();

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      // Upewnij się, że klasa jest usunięta przy unmount
      document.body.classList.remove('fullscreen-active');
      document.documentElement.classList.remove('fullscreen-active');
      const appElement = document.querySelector('.App');
      if (appElement) {
        appElement.classList.remove('fullscreen-active');
      }
    };
  }, []);

  // Synchronizuj ref z aktualnymi danymi kart
  useEffect(() => {
    cardsDataRef.current = cardsData;
  }, [cardsData]);

  // Funkcja do aktualizacji ostatniej aktywności użytkownika
  const updateLastActivity = () => {
    lastActivityRef.current = Date.now();
  };

  // Nasłuchuj aktywności użytkownika (kliknięcie, wpisywanie, ruch myszy)
  useEffect(() => {
    const handleActivity = () => {
      updateLastActivity();
    };

    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('scroll', handleActivity);

    return () => {
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('scroll', handleActivity);
    };
  }, []);

  // Funkcja do sprawdzania, czy użytkownik edytuje
  const isUserEditing = () => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    const hasFocusedInputs = focusedInputsRef.current.size > 0;
    
    // Jeśli użytkownik edytował w ciągu ostatnich 5 sekund lub ma otwarte pole
    return hasFocusedInputs || timeSinceLastActivity < 5000;
  };

  // Automatyczne odświeżanie danych z zabezpieczeniami
  useEffect(() => {
    const project = getCurrentProject();
    if (!project || !authAPI.isAuthenticated()) return;

    const autoRefresh = async () => {
      // Sprawdź, czy użytkownik edytuje
      if (isUserEditing()) {
        console.log('⏸️ Pomijam odświeżanie - użytkownik edytuje');
        return;
      }

      try {
        console.log('🔄 Automatyczne odświeżanie danych...');
        const savedCards = await dashboardAPI.getByProject(project);
        
        if (!savedCards || savedCards.length === 0) return;

        // Grupuj karty według numeru i wybierz najnowsze
        const cardsByNumber = {};
        savedCards.forEach(card => {
          const cardNum = card.cardNumber;
          if (!cardsByNumber[cardNum]) {
            cardsByNumber[cardNum] = [];
          }
          cardsByNumber[cardNum].push(card);
        });

        const defaultCardNumbers = ['01', '02', '03'];
        const formattedCards = [];
        
        defaultCardNumbers.forEach(cardNum => {
          if (cardsByNumber[cardNum] && cardsByNumber[cardNum].length > 0) {
            const sortedCards = cardsByNumber[cardNum].sort((a, b) => {
              const updatedA = a.updatedAt || a.createdAt || '';
              const updatedB = b.updatedAt || b.createdAt || '';
              if (updatedA !== updatedB) {
                return updatedB.localeCompare(updatedA);
              }
              const dateA = a.date || '';
              const dateB = b.date || '';
              return dateB.localeCompare(dateA);
            });
            
            const latestCard = sortedCards[0];
            
            // Porównaj timestamp - nadpisz tylko jeśli serwer ma nowsze dane
            const currentCard = cardsDataRef.current.find(c => c.cardNumber === cardNum);
            if (currentCard) {
              const currentUpdatedAt = currentCard.updatedAt || currentCard.createdAt || '';
              const serverUpdatedAt = latestCard.updatedAt || latestCard.createdAt || '';
              
              // Jeśli lokalne dane są nowsze, nie nadpisuj
              if (currentUpdatedAt && serverUpdatedAt && currentUpdatedAt > serverUpdatedAt) {
                console.log(`⏭️ Pomijam kartę ${cardNum} - lokalne dane są nowsze`);
                formattedCards.push(currentCard);
                return;
              }
            }

            formattedCards.push({
              id: latestCard.id,
              cardNumber: latestCard.cardNumber,
              trailerId: latestCard.trailerId !== undefined && latestCard.trailerId !== null ? latestCard.trailerId : '',
              date: latestCard.date !== undefined && latestCard.date !== null ? latestCard.date : '',
              time: latestCard.time !== undefined && latestCard.time !== null ? latestCard.time : '',
              code: latestCard.code !== undefined && latestCard.code !== null ? latestCard.code : 'PL',
              lastUpdateTime: latestCard.lastUpdateTime !== undefined && latestCard.lastUpdateTime !== null ? latestCard.lastUpdateTime : '',
              data: (latestCard.data && Array.isArray(latestCard.data))
                ? latestCard.data.length > 0
                  ? latestCard.data.map(row => ({
                      ...row,
                      check: normalizeCheckField(row.check),
                      seq: row.seq !== undefined && row.seq !== null ? row.seq : '',
                      j: row.j !== undefined && row.j !== null ? row.j : '',
                      opis: row.opis !== undefined && row.opis !== null ? (row.opis || row.description || '') : '',
                      assignedTo: row.assignedTo !== undefined ? row.assignedTo : null,
                      przypisanie: row.przypisanie !== undefined && row.przypisanie !== null ? row.przypisanie : (row.assignedTo !== undefined && row.assignedTo !== null ? row.assignedTo : null),
                      komentarz: row.komentarz !== undefined && row.komentarz !== null ? row.komentarz : null,
                      doZrobienia: row.doZrobienia !== undefined && row.doZrobienia !== null ? row.doZrobienia : null,
                      zrobione: row.zrobione !== undefined && row.zrobione !== null ? row.zrobione : null,
                      zainfekowane: row.zainfekowane !== undefined && row.zainfekowane !== null ? row.zainfekowane : null
                    }))
                  : []
                : []
            });
          } else {
            // Brak karty - użyj domyślnej
            const today = getTodayDate();
            formattedCards.push({
              cardNumber: cardNum,
              trailerId: '',
              date: today,
              time: cardNum === '01' ? '17:00' : cardNum === '02' ? '18:00' : '19:00',
              code: cardNum === '01' ? 'PL' : cardNum === '02' ? 'PA' : 'TT',
              lastUpdateTime: '', // Pusty czas ostatniej aktualizacji dla nowej karty
              data: []
            });
          }
        });

        formattedCards.sort((a, b) => a.cardNumber.localeCompare(b.cardNumber));
        const synchronizedCards = synchronizeRows(formattedCards);
        
        // Aktualizuj tylko jeśli dane się zmieniły
        setCardsData(prevCards => {
          const hasChanges = JSON.stringify(prevCards) !== JSON.stringify(synchronizedCards);
          if (hasChanges) {
            console.log('✅ Zaktualizowano dane z serwera');
            cardsDataRef.current = synchronizedCards;
            return synchronizedCards;
          }
          return prevCards;
        });
      } catch (error) {
        console.error('Błąd automatycznego odświeżania:', error);
      }
    };

    // Odświeżaj co 10 sekund
    const interval = setInterval(autoRefresh, 10000);
    
    // Odśwież od razu przy starcie (tylko jeśli użytkownik nie edytuje)
    setTimeout(() => {
      if (!isUserEditing()) {
        autoRefresh();
      }
    }, 5000); // Poczekaj 5 sekund po załadowaniu

    return () => clearInterval(interval);
  }, [user, projects]); // Nie dodajemy cardsData do dependencies, aby uniknąć nieskończonej pętli

  // Sortuj karty według daty i godziny - MUSI być przed wszystkimi warunkowymi returnami
  // Sortowanie odbywa się TYLKO dla kart z wypełnionymi wszystkimi trzema polami: Data, Godzina, Trailer
  const sortedCards = useMemo(() => {
    // Sprawdź czy cardsData istnieje i ma elementy
    if (!cardsData || cardsData.length === 0) {
      return [];
    }
    
    // Funkcja sortująca karty według daty i godziny
    return [...cardsData].sort((a, b) => {
      // Sprawdź, czy karty mają wypełnione wszystkie trzy pola: Data, Godzina, Trailer
      const aHasAllFields = a.date && a.date.trim() !== '' && 
                           a.time && a.time.trim() !== '' && 
                           a.trailerId && a.trailerId.trim() !== '';
      const bHasAllFields = b.date && b.date.trim() !== '' && 
                           b.time && b.time.trim() !== '' && 
                           b.trailerId && b.trailerId.trim() !== '';
      
      // Jeśli obie karty nie mają wszystkich pól, zachowaj oryginalną kolejność
      if (!aHasAllFields && !bHasAllFields) {
        return 0;
      }
      
      // Karty bez wszystkich trzech pól idą na koniec (z prawej strony)
      if (!aHasAllFields) return 1;
      if (!bHasAllFields) return -1;
      
      // Oba karty mają wszystkie pola - sortuj według daty i godziny
      // Porównaj daty (format YYYY-MM-DD można porównywać leksykograficznie)
      const dateComparison = a.date.localeCompare(b.date);
      if (dateComparison !== 0) {
        return dateComparison;
      }
      
      // Jeśli daty są równe, porównaj godziny (format HH:MM można porównywać leksykograficznie)
      const timeComparison = a.time.localeCompare(b.time);
      if (timeComparison !== 0) {
        return timeComparison;
      }
      
      // Jeśli daty i godziny są równe, zachowaj oryginalną kolejność (według cardNumber)
      return a.cardNumber.localeCompare(b.cardNumber);
    });
  }, [cardsData]);

  // Funkcja sprawdzająca, czy Data, Godzina i Trailer są wypełnione dla danej karty
  const isCardBasicFieldsFilled = (card) => {
    const hasDate = card.date && card.date.trim() !== '';
    const hasTime = card.time && card.time.trim() !== '';
    const hasTrailer = card.trailerId && card.trailerId.trim() !== '';
    return hasDate && hasTime && hasTrailer;
  };

  const handleDataChange = (cardIndex, rowIndex, field, value) => {
    // Sprawdź uprawnienie do edycji Dashboard
    if (!hasDashboardPermission(user, 'edit')) {
      alert('Nie masz uprawnień do edycji Dashboard.');
      return;
    }
    
    const updatedCards = [...cardsData];
    
    // Sprawdź, czy to pole wymaga aktualizacji czasu ostatniej zmiany
    const shouldUpdateTime = ['doZrobienia', 'zrobione', 'assignedTo', 'komentarz', 'ocz', 'up', 'upok', 'av', 'sk', 'er'].includes(field);
    
    // Utwórz nowy obiekt dla karty, aby React wykrył zmianę
    const currentCard = updatedCards[cardIndex];
    const updatedCard = { ...currentCard };
    
    // Utwórz nową tablicę danych z nowym obiektem wiersza
    const updatedData = [...currentCard.data];
    const updatedRow = { ...updatedData[rowIndex] };
    
    if (field === 'zainfekowane') {
      updatedRow.zainfekowane = value === '' ? null : parseInt(value) || null;
    } else if (field === 'doZrobienia') {
      updatedRow.doZrobienia = value === '' ? null : parseInt(value) || null;
      
      // Nie aktualizuj OCZ automatycznie - użytkownik może mieć własną wartość
      // OCZ będzie obliczane tylko do wyświetlenia, jeśli jest puste
      
      // Wyczyść błąd walidacji dla kolumny "Ilość", jeśli co najmniej jedno pole jest wypełnione
      updatedData[rowIndex] = updatedRow;
      const hasAnyQuantity = updatedData.some(row => {
        const qty = row.doZrobienia;
        return qty !== null && qty !== undefined && qty !== '';
      });
      
      if (hasAnyQuantity) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          if (newErrors[cardIndex]) {
            delete newErrors[cardIndex].quantity;
            if (Object.keys(newErrors[cardIndex]).length === 0) {
              delete newErrors[cardIndex];
            }
          }
          return newErrors;
        });
      }
    } else if (field === 'zrobione') {
      updatedRow.zrobione = value === '' ? null : parseInt(value) || null;
      
      // Nie aktualizuj OCZ automatycznie - użytkownik może mieć własną wartość
      // OCZ będzie obliczane tylko do wyświetlenia, jeśli jest puste
    } else if (field === 'assignedTo') {
      console.log('🔧 handleDataChange - assignedTo:', {
        cardIndex,
        rowIndex,
        oldValue: updatedData[rowIndex].assignedTo,
        newValue: value,
        j: updatedData[rowIndex].j
      });
      updatedRow.assignedTo = value;
    } else if (field === 'komentarz') {
      const existingComment = updatedRow.komentarz || '';
      const incoming = parseCommentMeta(value || '');
      const existing = parseCommentMeta(existingComment);
      const users = incoming.hasMeta ? incoming.users : existing.users;
      updatedRow.komentarz = buildCommentWithUsers(incoming.text, users, incoming.hasMeta);
    } else if (field === 'seq') {
      // WAŻNE: Zapisz zmianę seq do bazy danych
      updatedRow.seq = value || '';
    } else if (field === 'opis') {
      updatedRow.opis = value || '';
    } else if (field === 'ocz') {
      // OCZ może być edytowane ręcznie - zapisz dokładnie to, co użytkownik wprowadził (może być puste)
      updatedRow.ocz = value || '';
    } else if (field === 'up') {
      updatedRow.up = value || '';
    } else if (field === 'upok') {
      updatedRow.upok = value || '';
    } else if (field === 'av') {
      updatedRow.av = value || '';
    } else if (field === 'sk') {
      updatedRow.sk = value || '';
    } else if (field === 'er') {
      updatedRow.er = value || '';
    }
    
    // Zaktualizuj wiersz w tablicy danych
    updatedData[rowIndex] = updatedRow;
    updatedCard.data = updatedData;
    
    // Aktualizuj czas ostatniej zmiany dla pól: Ilość, Wyk, Przypisanie, Komentarz, OCZ, UP, UPok, AV, SK, ER
    if (shouldUpdateTime) {
      const now = new Date();
      const timeStr = now.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
      updatedCard.lastUpdateTime = timeStr;
    }
    
    // Zaktualizuj kartę w tablicy
    updatedCards[cardIndex] = updatedCard;
    
    setCardsData(updatedCards);
    saveCard(updatedCard, cardIndex);
  };

  // Funkcja do obsługi nawigacji klawiszami strzałek
  const handleKeyDown = (e, cardIndex, rowIndex, field) => {
    // Obsługuj tylko strzałki
    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      return;
    }

    e.preventDefault();
    
    const card = cardsData[cardIndex];
    if (!card || !card.data) return;

    const totalRows = card.data.length;
    let newRowIndex = rowIndex;
    let newField = field;

    switch (e.key) {
      case 'ArrowUp':
        // Przejdź do komórki powyżej (ten sam typ pola)
        newRowIndex = Math.max(0, rowIndex - 1);
        break;
      case 'ArrowDown':
        // Przejdź do komórki poniżej (ten sam typ pola)
        newRowIndex = Math.min(totalRows - 1, rowIndex + 1);
        break;
      case 'ArrowLeft':
        // Przejdź do komórki po lewej
        if (field === 'zrobione') {
          newField = 'doZrobienia';
        } else if (field === 'ocz') {
          newField = 'zrobione';
        } else if (field === 'up') {
          newField = 'ocz';
        } else if (field === 'upok') {
          newField = 'up';
        } else if (field === 'av') {
          newField = 'upok';
        } else if (field === 'sk') {
          newField = 'av';
        } else if (field === 'er') {
          newField = 'sk';
        }
        // Jeśli jesteśmy w 'doZrobienia', nie ma komórki po lewej (SEQ nie jest edytowalne)
        break;
      case 'ArrowRight':
        // Przejdź do komórki po prawej
        if (field === 'doZrobienia') {
          newField = 'zrobione';
        } else if (field === 'zrobione') {
          // Sprawdź czy karta ma włączone rozszerzone kolumny
          const card = cardsData[cardIndex];
          if (card && (showExtendedColumns[card.cardNumber] || false) && hasDashboardPermission(user, 'viewExtendedColumns')) {
            newField = 'ocz';
          }
        } else if (field === 'ocz') {
          newField = 'up';
        } else if (field === 'up') {
          newField = 'upok';
        } else if (field === 'upok') {
          newField = 'av';
        } else if (field === 'av') {
          newField = 'sk';
        } else if (field === 'sk') {
          newField = 'er';
        }
        // Jeśli jesteśmy w 'zrobione' i nie ma rozszerzonych kolumn, nie ma komórki po prawej (PRZYPISANIE nie jest inputem)
        break;
      default:
        return;
    }

    // Znajdź input do fokusa
    const inputId = `input-${cardIndex}-${newRowIndex}-${newField}`;
    setTimeout(() => {
      const nextInput = document.getElementById(inputId);
      if (nextInput) {
        nextInput.focus();
        nextInput.select(); // Zaznacz tekst w inputie
      }
    }, 0);
  };

  const updateDashboardZoom = (nextZoom) => {
    const clamped = Math.min(150, Math.max(50, nextZoom));
    setDashboardZoom(clamped);
    localStorage.setItem('dashboardZoom', String(clamped));
  };

  // Funkcja do wyciągnięcia tylko imienia z pełnego imienia i nazwiska
  const getFirstNameOnly = (fullName) => {
    if (!fullName) return '';
    // Podziel na słowa i weź pierwsze (imię)
    const parts = fullName.trim().split(/\s+/);
    return parts[0] || fullName;
  };

  // Funkcja do pobrania zmiany użytkownika na podstawie jego nazwy
  const getUserShift = (userName) => {
    if (!userName || !users || users.length === 0) return null;
    
    // Znajdź użytkownika po pełnej nazwie lub imieniu
    const user = users.find(u => {
      const fullName = u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim();
      const displayName = u.firstName && u.lastName 
        ? `${u.firstName} ${u.lastName}` 
        : fullName;
      return fullName === userName || displayName === userName || u.name === userName;
    });
    
    return user?.shift || null;
  };

  // Funkcja do pobrania koloru tła na podstawie zmiany
  const getShiftColor = (shift) => {
    switch (shift) {
      case 'A':
        return {
          background: '#483D8B',
          borderColor: '#5A4F9F',
          color: '#FFFFFF'
        };
      case 'B':
        return {
          background: '#b94646',
          borderColor: '#a03a3a',
          color: '#FFFFFF'
        };
      case 'C':
        return {
          background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
          borderColor: '#9C27B0',
          color: '#6A1B9A'
        };
      default:
        return {
          background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
          borderColor: '#2196F3',
          color: '#1565C0'
        };
    }
  };

  // Funkcja do sprawdzania, którzy użytkownicy są już przypisani
  const getAssignedUsers = () => {
    const assigned = new Set();
    cardsData.forEach(card => {
      if (card.data && Array.isArray(card.data)) {
        card.data.forEach(row => {
          if (row.assignedTo) {
            assigned.add(row.assignedTo);
          }
          if (row.komentarz) {
            const { users } = parseCommentMeta(row.komentarz);
            users.forEach(userName => assigned.add(userName));
          }
        });
      }
    });
    return assigned;
  };

  // Funkcja do filtrowania pracowników według wymagań dla wybranej zmiany w modalu
  const getFilteredEmployees = () => {
    if (!users || users.length === 0) return [];
    
    return users.filter(userItem => {
      // Tylko pracownicy (role === 'employee')
      if (userItem.role !== 'employee') return false;
      
      // Tylko z projektu MAN
      const userProjects = Array.isArray(userItem.projects) ? userItem.projects : (userItem.project ? [userItem.project] : []);
      if (!userProjects.includes('MAN')) return false;
      
      // Tylko ze stanowiskiem SEQ
      if (userItem.position !== 'SEQ') return false;
      
      // Filtruj według wybranej zmiany w modalu
      if (userItem.shift !== selectedShiftInModal) return false;
      
      return true;
    });
  };

  // Funkcje do obsługi drag and drop z modala
  const handleDragStart = (e, userName) => {
    setDraggedUser(userName);
    setDraggedFromPosition(null); // Z modala, nie z karty
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', userName);
  };

  // Funkcja do obsługi drag and drop przypisanego użytkownika z karty
  const handleAssignedUserDragStart = (e, userName, cardIndex, rowIndex) => {
    e.stopPropagation(); // Zapobiegaj propagacji do rodzica
    setDraggedUser(userName);
    setDraggedFromPosition({ cardIndex, rowIndex }); // Zapamiętaj skąd został przeciągnięty
    setDraggedFromComment(null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', userName);
  };

  const handleCommentUserDragStart = (e, userName, cardIndex, rowIndex) => {
    e.stopPropagation();
    setDraggedUser(userName);
    setDraggedFromPosition(null);
    setDraggedFromComment({ cardIndex, rowIndex, userName });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', userName);
  };

  const handleDragEnd = (e) => {
    if (e?.target?.style) {
      e.target.style.opacity = '';
    }
    setDraggedUser(null);
    setDraggedFromPosition(null);
    setDraggedFromComment(null);
    // Upewnij się, że modal nie jest w trybie przesuwania po zakończeniu drag
    setIsDraggingModal(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    // Dodaj klasę dla podświetlenia
    if (e.currentTarget && !e.currentTarget.classList.contains('drag-over')) {
      e.currentTarget.classList.add('drag-over');
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Usuń klasę podświetlenia tylko jeśli opuszczamy komórkę (nie wchodzimy w dziecko)
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      e.currentTarget.classList.remove('drag-over');
    }
  };

  const handleDrop = async (e, cardIndex, rowIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Sprawdź uprawnienie do edycji Dashboard
    if (!hasDashboardPermission(user, 'edit')) {
      alert('Nie masz uprawnień do przypisywania użytkowników.');
      return;
    }
    
    // Upewnij się, że modal nie jest w trybie przesuwania
    setIsDraggingModal(false);
    
    // Usuń klasę podświetlenia
    if (e.currentTarget) {
      e.currentTarget.classList.remove('drag-over');
    }
    
    const userName = e.dataTransfer.getData('text/plain') || draggedUser;
    if (userName) {
      // WAŻNE: Przed przypisaniem użytkownika, usuń go ze wszystkich innych miejsc
      // (zapobiega powielaniu użytkownika w wielu miejscach)
      const updatedCards = [...cardsData];
      const cardsToSave = new Set(); // Zbiór indeksów kart do zapisania
      
      // KROK 1: Sprawdź, czy w pozycji docelowej jest już inny użytkownik - usuń go
      const currentAssignedUser = updatedCards[cardIndex]?.data[rowIndex]?.assignedTo;
      if (currentAssignedUser && currentAssignedUser !== userName) {
        // W pozycji docelowej jest inny użytkownik - usuń go ze wszystkich miejsc
        console.log('🗑️ Usuwanie użytkownika z pozycji docelowej:', currentAssignedUser);
        updatedCards.forEach((card, cIdx) => {
          if (card.data && Array.isArray(card.data)) {
            card.data.forEach((row, rIdx) => {
              if (row.assignedTo === currentAssignedUser) {
                updatedCards[cIdx].data[rIdx].assignedTo = null;
                cardsToSave.add(cIdx);
              }
            });
          }
        });
      }
      
      // KROK 2: Usuń przeciąganego użytkownika ze wszystkich innych miejsc (oprócz pozycji docelowej)
      updatedCards.forEach((card, cIdx) => {
        if (card.data && Array.isArray(card.data)) {
          card.data.forEach((row, rIdx) => {
            // Jeśli to nie jest miejsce docelowe i użytkownik jest tam przypisany, usuń go
            if ((cIdx !== cardIndex || rIdx !== rowIndex) && row.assignedTo === userName) {
              console.log(`🗑️ Usuwanie użytkownika ${userName} z pozycji L${card.cardNumber} J${row.j}`);
              updatedCards[cIdx].data[rIdx].assignedTo = null;
              cardsToSave.add(cIdx); // Oznacz kartę do zapisania
            }
          });
        }
      });
      
      // KROK 3: Jeśli użytkownik był przeciągnięty z innej pozycji, upewnij się, że został usunięty stamtąd
      if (draggedFromPosition) {
        const { cardIndex: fromCardIndex, rowIndex: fromRowIndex } = draggedFromPosition;
        // Sprawdź czy to nie ta sama pozycja
        if (fromCardIndex !== cardIndex || fromRowIndex !== rowIndex) {
          // Usuń przypisanie z poprzedniej pozycji (jeśli jeszcze nie zostało usunięte w pętli powyżej)
          if (updatedCards[fromCardIndex]?.data[fromRowIndex]?.assignedTo === userName) {
            console.log(`🗑️ Usuwanie użytkownika ${userName} z pozycji źródłowej L${updatedCards[fromCardIndex].cardNumber} J${updatedCards[fromCardIndex].data[fromRowIndex].j}`);
            updatedCards[fromCardIndex].data[fromRowIndex].assignedTo = null;
            cardsToSave.add(fromCardIndex); // Oznacz kartę do zapisania
          }
        }
      }

      // KROK 3b: Jeśli użytkownik był przeciągnięty z komentarza, usuń go z komentarza źródłowego
      if (draggedFromComment) {
        const { cardIndex: fromCardIndex, rowIndex: fromRowIndex } = draggedFromComment;
        const sourceRow = updatedCards[fromCardIndex]?.data[fromRowIndex];
        if (sourceRow?.komentarz) {
          const updatedComment = removeUserFromComment(sourceRow.komentarz, userName);
          if (updatedComment !== sourceRow.komentarz) {
            updatedCards[fromCardIndex].data[fromRowIndex].komentarz = updatedComment;
            cardsToSave.add(fromCardIndex);
          }
        }
      }
      
      // KROK 4: Przypisz użytkownika do nowej pozycji
      if (updatedCards[cardIndex]?.data[rowIndex]) {
        console.log(`✅ Przypisywanie użytkownika ${userName} do pozycji L${updatedCards[cardIndex].cardNumber} J${updatedCards[cardIndex].data[rowIndex].j}`);
        updatedCards[cardIndex].data[rowIndex].assignedTo = userName;
        
        // Aktualizuj czas ostatniej zmiany (przypisanie użytkownika)
        const now = new Date();
        const timeStr = now.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
        updatedCards[cardIndex].lastUpdateTime = timeStr;
        
        cardsToSave.add(cardIndex); // Oznacz kartę do zapisania
      }
      
      // Zaktualizuj stan
      setCardsData(updatedCards);
      
      // Zapisz wszystkie zmienione karty SYNCHRONICZNIE (czekamy na wszystkie zapisy)
      // WAŻNE: Używamy Promise.all aby upewnić się, że wszystkie zapisy są zakończone
      // i używamy immediate = true aby zapisać natychmiast bez debouncing
      try {
        const savePromises = Array.from(cardsToSave).map(cardIdx => {
          console.log(`💾 Zapisywanie karty L${updatedCards[cardIdx].cardNumber} (indeks: ${cardIdx})`);
          return saveCard(updatedCards[cardIdx], cardIdx, true); // immediate = true
        });
        
        // Czekaj na zakończenie wszystkich zapisów
        await Promise.all(savePromises);
        console.log('✅ Wszystkie karty zostały zapisane pomyślnie');
      } catch (error) {
        console.error('❌ Błąd podczas zapisywania kart:', error);
        alert('Nie udało się zapisać zmian. Spróbuj ponownie.');
      }
      
      setDraggedUser(null);
      setDraggedFromPosition(null);
      setDraggedFromComment(null);
    }
  };

  const parseCommentMeta = (rawComment) => {
    const raw = rawComment || '';
    const match = raw.match(/\[\[USR:([^\]]*)\]\]$/);
    if (!match) {
      return { text: raw, users: [], hasMeta: false };
    }
    const users = match[1]
      .split(';')
      .map((name) => name.trim())
      .filter(Boolean);
    const text = raw.replace(/\s*\[\[USR:[^\]]*\]\]\s*$/, '');
    return { text, users, hasMeta: true };
  };

  const buildCommentWithUsers = (text, users, forceMeta = false) => {
    const rawText = text || '';
    const cleanUsers = Array.from(
      new Set((users || []).map((name) => name.trim()).filter(Boolean))
    );
    if (cleanUsers.length === 0) {
      if (!forceMeta) {
        // Zachowaj spacje wpisywane przez użytkownika podczas edycji komentarza
        return rawText;
      }
      const trimmed = rawText.trimEnd();
      return `${trimmed}${trimmed ? ' ' : ''}[[USR:]]`;
    }
    const trimmed = rawText.trimEnd();
    return `${trimmed}${trimmed ? ' ' : ''}[[USR:${cleanUsers.join(';')}]]`;
  };

  const addUserToComment = (rawComment, userName) => {
    if (!userName) return rawComment || '';
    const { text, users } = parseCommentMeta(rawComment);
    const exists = users.some((u) => u.toLowerCase() === userName.toLowerCase());
    if (exists) {
      return buildCommentWithUsers(text, users, true);
    }
    return buildCommentWithUsers(text, [...users, userName], true);
  };

  const removeUserFromComment = (rawComment, userName) => {
    if (!userName) return rawComment || '';
    const { text, users } = parseCommentMeta(rawComment);
    const filtered = users.filter(
      (u) => u.toLowerCase() !== userName.toLowerCase()
    );
    return buildCommentWithUsers(text, filtered, true);
  };

  const handleCommentDrop = async (e, cardIndex, rowIndex) => {
    e.preventDefault();
    e.stopPropagation();

    if (!hasDashboardPermission(user, 'edit') || !isCardBasicFieldsFilled(cardsData[cardIndex])) {
      return;
    }

    if (e.currentTarget) {
      e.currentTarget.classList.remove('drag-over');
    }

    const userName = e.dataTransfer.getData('text/plain') || draggedUser;
    if (!userName) return;

    const updatedCards = [...cardsData];
    const cardsToSave = new Set();
    const now = new Date();
    const timeStr = now.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });

    // Usuń użytkownika z przypisania (assignment) we wszystkich kartach
    updatedCards.forEach((card, cIdx) => {
      if (card.data && Array.isArray(card.data)) {
        card.data.forEach((row, rIdx) => {
          if (row.assignedTo === userName) {
            updatedCards[cIdx].data[rIdx].assignedTo = null;
            updatedCards[cIdx].lastUpdateTime = timeStr;
            cardsToSave.add(cIdx);
          }
        });
      }
    });

    // Usuń użytkownika z komentarza źródłowego, jeśli przenosimy z innego miejsca
    if (draggedFromComment) {
      const { cardIndex: fromCardIndex, rowIndex: fromRowIndex } = draggedFromComment;
      if (fromCardIndex !== cardIndex || fromRowIndex !== rowIndex) {
        const sourceRow = updatedCards[fromCardIndex]?.data[fromRowIndex];
        if (sourceRow?.komentarz) {
          const updatedComment = removeUserFromComment(sourceRow.komentarz, userName);
          if (updatedComment !== sourceRow.komentarz) {
            updatedCards[fromCardIndex].data[fromRowIndex].komentarz = updatedComment;
            updatedCards[fromCardIndex].lastUpdateTime = timeStr;
            cardsToSave.add(fromCardIndex);
          }
        }
      }
    }

    // Dodaj użytkownika do komentarza docelowego
    const targetRow = updatedCards[cardIndex]?.data[rowIndex];
    if (targetRow) {
      const currentComment = targetRow.komentarz || '';
      const nextComment = addUserToComment(currentComment, userName);
      if (nextComment !== currentComment) {
        updatedCards[cardIndex].data[rowIndex].komentarz = nextComment;
        updatedCards[cardIndex].lastUpdateTime = timeStr;
        cardsToSave.add(cardIndex);
      }
    }

    if (cardsToSave.size > 0) {
      setCardsData(updatedCards);
      try {
        await Promise.all(
          Array.from(cardsToSave).map(cardIdx => saveCard(updatedCards[cardIdx], cardIdx, true))
        );
      } catch (error) {
        console.error('❌ Błąd podczas zapisywania kart po przypisaniu do komentarza:', error);
      }
    }
  };

  // Funkcje do przesuwania modala
  const handleModalMouseDown = (e) => {
    // Nie aktywuj przesuwania jeśli:
    // 1. Kliknięto w przycisk zamknięcia
    // 2. Kliknięto w element draggable (użytkownika)
    // 3. Kliknięto w obszar, który nie jest nagłówkiem modala
    if (e.target.closest('.modal-close-btn')) return;
    if (e.target.closest('.user-item')) return;
    if (!e.target.closest('.assignment-modal-header')) return;
    
    // Sprawdź czy nie jesteśmy w trakcie drag and drop
    if (draggedUser) return;
    
    setIsDraggingModal(true);
    const modalContent = e.currentTarget.closest('.assignment-modal-content');
    if (modalContent) {
      const rect = modalContent.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleModalMouseMove = (e) => {
    if (isDraggingModal) {
      setModalPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  };

  const handleModalMouseUp = () => {
    setIsDraggingModal(false);
  };

  // Funkcje do obsługi przeciągania ikony użytkownika
  const handleUserIconMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingUserIcon(true);
    hasMovedDuringDragRef.current = false;
    const rect = e.currentTarget.getBoundingClientRect();
    setUserIconDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    // Zapisz początkową pozycję ikony
    userIconStartPositionRef.current = {
      x: userIconPosition.x,
      y: userIconPosition.y
    };
  };

  const handleUserIconMouseMove = (e) => {
    if (isDraggingUserIcon) {
      const newPosition = {
        x: e.clientX - userIconDragOffset.x,
        y: e.clientY - userIconDragOffset.y
      };
      
      // Sprawdź, czy ikona faktycznie się przesunęła (więcej niż 5px)
      const movedX = Math.abs(newPosition.x - userIconStartPositionRef.current.x);
      const movedY = Math.abs(newPosition.y - userIconStartPositionRef.current.y);
      if (movedX > 5 || movedY > 5) {
        hasMovedDuringDragRef.current = true;
      }
      
      setUserIconPosition(newPosition);
    }
  };

  const handleUserIconMouseUp = () => {
    if (isDraggingUserIcon) {
      setIsDraggingUserIcon(false);
    }
  };

  // Efekt do zapisywania pozycji ikony użytkownika do localStorage
  useEffect(() => {
    if (!isDraggingUserIcon && userIconPosition.x !== 0 && userIconPosition.y !== 0) {
      localStorage.setItem('userIconPosition', JSON.stringify(userIconPosition));
    }
  }, [userIconPosition, isDraggingUserIcon]);

  useEffect(() => {
    if (isDraggingModal) {
      document.addEventListener('mousemove', handleModalMouseMove);
      document.addEventListener('mouseup', handleModalMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleModalMouseMove);
        document.removeEventListener('mouseup', handleModalMouseUp);
      };
    }
  }, [isDraggingModal, dragOffset]);

  // Efekt do obsługi przeciągania ikony użytkownika
  useEffect(() => {
    if (isDraggingUserIcon) {
      document.addEventListener('mousemove', handleUserIconMouseMove);
      document.addEventListener('mouseup', handleUserIconMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleUserIconMouseMove);
        document.removeEventListener('mouseup', handleUserIconMouseUp);
      };
    }
  }, [isDraggingUserIcon, userIconDragOffset, userIconPosition]);

  // Funkcja do generowania nowego SEQ (następny numer)
  const getNextSeq = () => {
    if (cardsData.length === 0 || cardsData[0].data.length === 0) {
      return 'C32033';
    }
    const allSeqs = cardsData[0].data.map(row => row.seq);
    const numbers = allSeqs.map(seq => {
      const match = seq.match(/C320(\d+)/);
      return match ? parseInt(match[1]) : 0;
    });
    const maxNum = Math.max(...numbers, 32);
    const nextNum = maxNum + 1;
    return `C320${String(nextNum).padStart(2, '0')}`;
  };

  // Dodaj nową pozycję do wszystkich kart
  const handleAddRow = async () => {
    // Sprawdź uprawnienie do dodawania wierszy
    if (!hasDashboardPermission(user, 'addRows')) {
      alert('Nie masz uprawnień do dodawania wierszy.');
      return;
    }
    
    try {
      // Sprawdź, czy karty są załadowane
      if (!cardsData || cardsData.length === 0) {
        console.error('❌ Brak kart do aktualizacji');
        alert('Brak kart. Odśwież stronę i spróbuj ponownie.');
        return;
      }

      // WAŻNE: Nie generuj domyślnego SEQ - użytkownik powinien sam wypełnić wartości
      // Nowy wiersz powinien mieć puste wartości, które użytkownik może wypełnić
      const newRow = {
        check: '',
        seq: '', // Puste - użytkownik wypełni sam
        j: '', // Puste - użytkownik wypełni sam
        doZrobienia: null,
        zrobione: null,
        zainfekowane: null,
        komentarz: '',
        opis: '' // Puste - użytkownik wypełni sam
      };

      const updatedCards = cardsData.map(card => ({
        ...card,
        data: [
          ...(card.data || []),
          newRow
        ]
      }));
      
      console.log('➕ Dodawanie pojedynczego wiersza:', {
        przedSynchronizacja: updatedCards.map(card => ({
          cardNumber: card.cardNumber,
          dataLength: card.data?.length || 0,
          lastRow: card.data?.[card.data.length - 1] ? { seq: card.data[card.data.length - 1].seq, j: card.data[card.data.length - 1].j, opis: card.data[card.data.length - 1].opis } : null
        }))
      }); // Debug
      
      // Synchronizuj liczbę wierszy (wszystkie karty powinny mieć taką samą liczbę)
      const synchronizedCards = synchronizeRows(updatedCards);
      
      console.log('➕ Po synchronizacji:', {
        poSynchronizacji: synchronizedCards.map(card => ({
          cardNumber: card.cardNumber,
          dataLength: card.data?.length || 0,
          allSeqs: card.data?.map(r => r.seq) || [],
          lastRow: card.data?.[card.data.length - 1] ? { seq: card.data[card.data.length - 1].seq, j: card.data[card.data.length - 1].j, opis: card.data[card.data.length - 1].opis } : null
        }))
      }); // Debug
      
      setCardsData(synchronizedCards);
      
      // Zapisz wszystkie karty NATYCHMIAST (bez debouncingu) - ważne dla operacji dodawania
      for (let i = 0; i < synchronizedCards.length; i++) {
        await saveCard(synchronizedCards[i], i, true); // immediate = true
      }
      
      console.log('✅ Wszystkie karty zapisane po dodaniu pojedynczego wiersza'); // Debug
    } catch (error) {
      console.error('❌ Błąd podczas dodawania wiersza:', error);
      alert('Wystąpił błąd podczas dodawania wiersza. Sprawdź konsolę przeglądarki.');
    }
  };

  // Dodaj wiele pozycji z listy (wklejonej)
  const handleBulkAdd = () => {
    if (!bulkSeqInput.trim()) {
      alert('Wprowadź listę pozycji SEQ');
      return;
    }

    // Podziel tekst na linie i wyczyść
    const seqList = bulkSeqInput
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    if (seqList.length === 0) {
      alert('Lista pozycji jest pusta');
      return;
    }

    // Sprawdź, które pozycje już istnieją
    const existingSeqs = cardsData[0]?.data.map(row => row.seq) || [];
    const existingSeqsSet = new Set(existingSeqs);
    
    // Filtruj tylko nowe pozycje, zachowując kolejność wklejenia
    const newSeqs = seqList.filter(seq => !existingSeqsSet.has(seq));

    if (newSeqs.length === 0) {
      alert('Wszystkie pozycje już istnieją');
      setBulkSeqInput('');
      return;
    }

    // Dodaj nowe pozycje do wszystkich kart w kolejności wklejenia (na końcu)
    // WAŻNE: Zachowaj dokładną kolejność wklejenia
    const updatedCards = cardsData.map(card => ({
      ...card,
      data: [
        ...(card.data || []),
        // Dodaj nowe pozycje w DOKŁADNEJ kolejności wklejenia (nie sortuj!)
        ...newSeqs.map(seq => ({
          check: '',
          seq: seq,
          j: '',
          doZrobienia: null,
          zrobione: null,
          zainfekowane: null,
          komentarz: '',
          opis: ''
        }))
      ]
    }));

    // Synchronizuj liczbę wierszy - użyj synchronizeRows, która synchronizuje J, SEQ i opis z pierwszej karty
    // (pierwsza karta ma już prawidłową kolejność wklejenia)
    const synchronizedCards = synchronizeRows(updatedCards);
    
    console.log('➕ Dodawanie nowych wierszy:', {
      newSeqsCount: newSeqs.length,
      newSeqs: newSeqs,
      przedSynchronizacja: updatedCards.map(card => ({
        cardNumber: card.cardNumber,
        dataLength: card.data?.length || 0,
        allSeqs: card.data?.map(r => r.seq) || []
      })),
      poSynchronizacji: synchronizedCards.map(card => ({
        cardNumber: card.cardNumber,
        dataLength: card.data?.length || 0,
        allSeqs: card.data?.map(r => r.seq) || []
      }))
    }); // Debug
    
    setCardsData(synchronizedCards);
    setBulkSeqInput('');

    // Zapisz wszystkie karty NATYCHMIAST (bez debouncingu) - ważne dla operacji dodawania
    (async () => {
      for (let i = 0; i < synchronizedCards.length; i++) {
        await saveCard(synchronizedCards[i], i, true); // immediate = true
      }
      console.log('✅ Wszystkie karty zapisane po dodaniu wierszy'); // Debug
    })();

    alert(`Dodano ${newSeqs.length} nowych pozycji`);
  };

  // Funkcja do synchronizacji liczby wierszy między wszystkimi kartami
  // WAŻNE: Synchronizuje również dane J, SEQ i opis między wszystkimi kartami
  // WAŻNE: Zachowuje kolejność z pierwszej karty (która ma prawidłową kolejność wklejenia)
  // WAŻNE: Zachowuje seq z istniejących danych (nie nadpisuje!)
  const synchronizeRows = (cards) => {
    if (cards.length === 0) return cards;
    
    // Użyj pierwszej karty jako wzorca dla J, SEQ i opis (synchronizuj te dane między wszystkimi kartami)
    const baseCard = cards[0];
    const baseData = baseCard.data || [];
    
    // WAŻNE: Jeśli pierwsza karta ma pustą tablicę [], wszystkie karty powinny mieć pustą tablicę
    // (użytkownik usunął wszystkie wiersze przez modal)
    // WAŻNE: Zachowaj lastUpdateTime - nie czyść go podczas synchronizacji
    if (baseData.length === 0) {
      return cards.map(card => ({
        ...card,
        data: []
        // lastUpdateTime jest zachowane przez ...card
      }));
    }
    
    // Znajdź najdłuższą listę danych (najwięcej wierszy)
    const maxLength = Math.max(...cards.map(card => (card.data || []).length));
    
    // Synchronizuj dane J, SEQ i opis z pierwszej karty do wszystkich pozostałych kart
    const synchronizedCards = cards.map((card, cardIndex) => {
      if (cardIndex === 0) {
        // Pierwsza karta jest wzorcem - zwróć ją bez zmian
        return card;
      }
      
      // Dla pozostałych kart: użyj danych J, SEQ i opis z pierwszej karty
      // WAŻNE: Użyj baseData.length jako długości docelowej (nie maxLength)
      // To zapewnia, że wszystkie karty mają dokładnie tyle wierszy, ile ma pierwsza karta
      const newData = [];
      
      for (let index = 0; index < baseData.length; index++) {
        const baseRow = baseData[index];
        const existingRow = card.data && card.data[index] ? card.data[index] : null;
        
        // WAŻNE: Użyj J, SEQ i opis z pierwszej karty (wzorcowej) - to są dane zdefiniowane w modalu
        // Ale jeśli w pierwszej karcie są puste wartości, użyj wartości z istniejącej karty (jeśli istnieje)
        // To zapewnia, że dane z modalu są synchronizowane, ale nie nadpisujemy wartości użytkownika, jeśli są już zapisane
        // WAŻNE: Zawsze używaj J, SEQ i opis z pierwszej karty (wzorcowej) - to są dane zdefiniowane w modalu
        // Pierwsza karta (L01) jest zawsze wzorcem - jej wartości są kopiowane do wszystkich innych kart
        // To zapewnia, że dane z modalu są synchronizowane między wszystkimi kartami
        newData.push({
          check: normalizeCheckField(baseRow.check),
          // Zawsze używaj seq z pierwszej karty (wzorcowej) - to są dane z modalu
          seq: baseRow.seq !== undefined && baseRow.seq !== null ? baseRow.seq : '',
          // Zawsze używaj j z pierwszej karty (wzorcowej) - to są dane z modalu
          j: baseRow.j !== undefined && baseRow.j !== null ? baseRow.j : '',
          // Zawsze używaj opis z pierwszej karty (wzorcowej) - to są dane z modalu
          opis: baseRow.opis !== undefined && baseRow.opis !== null ? baseRow.opis : '',
          // Zachowaj dane specyficzne dla tej karty (doZrobienia, zrobione, przypisanie, komentarz)
          doZrobienia: existingRow?.doZrobienia !== undefined ? existingRow.doZrobienia : null,
          zrobione: existingRow?.zrobione !== undefined ? existingRow.zrobione : null,
          zainfekowane: existingRow?.zainfekowane !== undefined ? existingRow.zainfekowane : null,
          komentarz: existingRow?.komentarz !== undefined ? existingRow.komentarz : null,
          przypisanie: existingRow?.przypisanie !== undefined ? existingRow.przypisanie : null,
          assignedTo: existingRow?.assignedTo !== undefined ? existingRow.assignedTo : null,
          // OCZ - zachowaj wartość wprowadzoną przez użytkownika (może być puste)
          // Obliczanie automatyczne jest tylko do wyświetlenia w UI, nie zapisujemy go do danych
          ocz: existingRow?.ocz !== undefined && existingRow?.ocz !== null ? existingRow.ocz : '',
          up: existingRow?.up !== undefined ? existingRow.up : '',
          upok: existingRow?.upok !== undefined ? existingRow.upok : '',
          av: existingRow?.av !== undefined ? existingRow.av : '',
          sk: existingRow?.sk !== undefined ? existingRow.sk : '',
          er: existingRow?.er !== undefined ? existingRow.er : ''
        });
      }
      
      // WAŻNE: Nie dodawaj dodatkowych wierszy - wszystkie karty powinny mieć dokładnie tyle wierszy, ile ma pierwsza karta
      // (baseData.length)
      
      // WAŻNE: Zachowaj lastUpdateTime - nie nadpisuj go podczas synchronizacji
      return { 
        ...card, 
        data: newData
        // lastUpdateTime jest zachowane przez ...card
      };
    });
    
    return synchronizedCards;
  };

  // Funkcja do obsługi wklejania danych (paste) z Excela
  const handlePaste = (e, startRowIndex, startField, selectedCellState, cardIndex = 0) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    if (!pastedData) return;

    // Podziel wklejone dane na wiersze (usuń puste wiersze na końcu)
    const rows = pastedData.split(/\r?\n/).filter(row => row.trim() || row.includes('\t'));
    if (rows.length === 0) return;

    // Użyj zaznaczonej komórki jako punktu startowego, jeśli dostępna
    // WAŻNE: Używamy startRowIndex i startField z parametrów, nie selectedCellState,
    // ponieważ selectedCellState może wskazywać na inną kartę
    const actualStartRow = startRowIndex;
    const actualStartField = startField;

    // Dla każdego wiersza, podziel na komórki (tab - Excel używa tabulatorów)
    const parsedData = rows.map(row => {
      // Obsługa tabulatorów (Excel) lub średników
      const cells = row.split(/\t|;/).map(cell => cell.trim());
      return cells;
    });

    // Określ maksymalną liczbę kolumn w wklejonych danych
    const maxColumns = parsedData.length > 0 ? Math.max(...parsedData.map(row => row.length)) : 0;
    
    // Sprawdź, czy "Kolumny rozszerzone" są włączone dla aktualnej karty
    const currentCard = cardsData[cardIndex];
    const areExtendedColumnsVisible = (showExtendedColumns[currentCard?.cardNumber] || false) && hasDashboardPermission(user, 'viewExtendedColumns');
    
    // Określ, które pola są edytowalne w zależności od startField
    const isExtendedField = ['ocz', 'up', 'upok', 'av', 'sk', 'er'].includes(actualStartField);
    const isQuantityField = ['doZrobienia', 'zrobione'].includes(actualStartField); // Kolumny Ilość i WYK
    const isBasicField = ['check', 'j', 'seq', 'opis'].includes(actualStartField);
    
    // Jeśli "Kolumny rozszerzone" są włączone, określ wszystkie dostępne kolumny
    // Kolejność: Ilość (doZrobienia), WYK (zrobione), OCZ, UP, UPok, AV, SK, ER
    const allVisibleFields = areExtendedColumnsVisible 
      ? ['doZrobienia', 'zrobione', 'ocz', 'up', 'upok', 'av', 'sk', 'er']
      : ['doZrobienia', 'zrobione'];
    
    // Funkcja pomocnicza do filtrowania zer (nie wklejaj wartości 0)
    const filterZero = (value) => {
      if (!value || value.trim() === '') return '';
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return value; // Jeśli nie jest liczbą, zwróć oryginalną wartość
      if (numValue === 0) return ''; // Jeśli jest 0, zwróć pusty string
      return value; // W przeciwnym razie zwróć oryginalną wartość
    };
    
    // Zaktualizuj karty z wklejonymi danymi
    const updatedCards = cardsData.map((card, idx) => {
      // Dla kolumn podstawowych (J, SEQ, Opis) - wklej do wszystkich kart
      if (isBasicField) {
        // Przetwarzaj wszystkie karty dla kolumn podstawowych - kontynuuj
      } else {
        // Dla nowych kolumn (UP, UPok, AV, SK, ER) i kolumn Ilość/WYK - wklej tylko do aktualnej karty
        if (idx !== cardIndex) {
          return card; // Nie modyfikuj innych kart
        }
      }
      
      const newData = [...(card.data || [])];
      
      parsedData.forEach((cells, rowIndex) => {
        const targetRowIndex = actualStartRow + rowIndex;
        
        // Sprawdź, czy w tym wierszu są jakieś niepuste dane
        const hasData = cells.some(cell => cell && cell.trim() !== '');
        if (!hasData) return; // Pomiń puste wiersze
        
        // WAŻNE: Nie dodawaj nowych wierszy! Ilość wierszy jest kontrolowana tylko przez modal "Zarządzanie pozycjami SEQ"
        // Jeśli wiersz nie istnieje, pomiń wklejanie do tego wiersza
        if (targetRowIndex >= newData.length || targetRowIndex < 0) {
          return; // Pomiń wklejanie - wiersz nie istnieje
        }
        
        // Wklej dane do odpowiednich kolumn (jak w Excel - wielokolumnowe, wielowierszowe)
        if (isExtendedField) {
          // Wklejamy do kolumn rozszerzonych (OCZ, UP, UPok, AV, SK, ER) - tylko do aktualnej karty
          const extendedFields = ['ocz', 'up', 'upok', 'av', 'sk', 'er'];
          const startIndex = extendedFields.indexOf(actualStartField);
          const updatedRow = { ...newData[targetRowIndex] };
          
          cells.forEach((cell, cellIndex) => {
            const fieldIndex = startIndex + cellIndex;
            if (fieldIndex >= 0 && fieldIndex < extendedFields.length) {
              const filteredValue = filterZero(cell ?? '');
              updatedRow[extendedFields[fieldIndex]] = filteredValue;
            }
          });
          newData[targetRowIndex] = updatedRow;
        } else if (isQuantityField) {
          // Wklejamy do kolumn Ilość (doZrobienia) i WYK (zrobione) - tylko do aktualnej karty
          // Jeśli "Kolumny rozszerzone" są włączone, obsługuj również kolumny OCZ, UP, UPok, AV, SK, ER
          const fieldsToUse = areExtendedColumnsVisible 
            ? ['doZrobienia', 'zrobione', 'ocz', 'up', 'upok', 'av', 'sk', 'er']
            : ['doZrobienia', 'zrobione'];
          const startIndex = fieldsToUse.indexOf(actualStartField);
          const updatedRow = { ...newData[targetRowIndex] };
          
          cells.forEach((cell, cellIndex) => {
            const fieldIndex = startIndex + cellIndex;
            if (fieldIndex >= 0 && fieldIndex < fieldsToUse.length) {
              const fieldName = fieldsToUse[fieldIndex];
              const filteredValue = filterZero(cell ?? '');
              
              // Dla pól numerycznych (doZrobienia, zrobione) konwertuj na liczbę
              if (fieldName === 'doZrobienia' || fieldName === 'zrobione') {
                const numValue = filteredValue === '' ? null : (parseInt(filteredValue) || null);
                updatedRow[fieldName] = numValue;
              } else {
                // Dla pozostałych pól (OCZ, UP, UPok, AV, SK, ER) zapisz jako tekst
                updatedRow[fieldName] = filteredValue;
              }
            }
          });
          newData[targetRowIndex] = updatedRow;
        } else if (isBasicField) {
          // Wklejamy do kolumn podstawowych (Check, J, SEQ, Opis) - synchronizuj między wszystkimi kartami
          // WAŻNE: Dla kolumn podstawowych wklejamy do wszystkich kart, ale tylko jeśli wiersz istnieje
          const basicFields = ['check', 'j', 'seq', 'opis'];
          const startIndex = basicFields.indexOf(actualStartField);
          const updatedRow = { ...newData[targetRowIndex] };
          
          cells.forEach((cell, cellIndex) => {
            const fieldIndex = startIndex + cellIndex;
            if (fieldIndex >= 0 && fieldIndex < basicFields.length) {
              const f = basicFields[fieldIndex];
              updatedRow[f] = f === 'check' ? normalizeCheckField(cell) : (cell ?? '');
            }
          });
          newData[targetRowIndex] = updatedRow;
        }
      });
      
      return {
        ...card,
        data: newData
      };
    });

    // Synchronizuj tylko kolumny podstawowe (J, SEQ, Opis) między kartami
    // WAŻNE: synchronizeRows kopiuje J, SEQ, Opis z pierwszej karty do wszystkich innych
    // Ale NIE synchronizuje nowych kolumn (UP, UPok, AV, SK, ER)
    const synchronizedCards = synchronizeRows(updatedCards);
    
    // Sprawdź, czy wklejane dane dotyczą pól wymagających aktualizacji czasu
    // Pola wymagające aktualizacji: doZrobienia, zrobione, assignedTo, komentarz, ocz, up, upok, av, sk, er
    const fieldsRequiringTimeUpdate = ['doZrobienia', 'zrobione', 'assignedTo', 'komentarz', 'ocz', 'up', 'upok', 'av', 'sk', 'er'];
    const shouldUpdateTime = fieldsRequiringTimeUpdate.includes(actualStartField) || 
                             (isQuantityField && areExtendedColumnsVisible) || 
                             isExtendedField;
    
    // Aktualizuj czas ostatniej zmiany dla kart, które zostały zmodyfikowane
    if (shouldUpdateTime) {
      const now = new Date();
      // Użyj lokalnej strefy czasowej, ale upewnij się, że jest to czas lokalny (nie UTC)
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const timeStr = `${hours}:${minutes}`;
      
      // Aktualizuj czas dla karty, do której wklejono dane
      if (synchronizedCards[cardIndex]) {
        synchronizedCards[cardIndex].lastUpdateTime = timeStr;
      }
      
      // Jeśli wklejano do kolumn podstawowych (J, SEQ, Opis), aktualizuj czas dla wszystkich kart
      if (isBasicField) {
        synchronizedCards.forEach(card => {
          card.lastUpdateTime = timeStr;
        });
      }
    }
    
    setCardsData(synchronizedCards);
    
    // Zapisz wszystkie karty NATYCHMIAST (bez debouncingu) - ważne dla operacji wklejania
    (async () => {
      for (let i = 0; i < synchronizedCards.length; i++) {
        await saveCard(synchronizedCards[i], i, true); // immediate = true
      }
    })();
    
    // Zaznacz ostatnią wklejoną komórkę
    const lastRowIndex = actualStartRow + parsedData.length - 1;
    let startFieldIndex, lastColumnIndex, lastField;
    
    if (isExtendedField) {
      startFieldIndex = ['ocz', 'up', 'upok', 'av', 'sk', 'er'].indexOf(actualStartField);
      lastColumnIndex = Math.min(startFieldIndex + maxColumns - 1, 5);
      lastField = ['ocz', 'up', 'upok', 'av', 'sk', 'er'][lastColumnIndex] || actualStartField;
    } else if (isQuantityField) {
      const fieldsToUse = areExtendedColumnsVisible 
        ? ['doZrobienia', 'zrobione', 'ocz', 'up', 'upok', 'av', 'sk', 'er']
        : ['doZrobienia', 'zrobione'];
      startFieldIndex = fieldsToUse.indexOf(actualStartField);
      lastColumnIndex = Math.min(startFieldIndex + maxColumns - 1, fieldsToUse.length - 1);
      lastField = fieldsToUse[lastColumnIndex] || actualStartField;
    } else {
      const basicFieldsOrder = ['check', 'j', 'seq', 'opis'];
      startFieldIndex = basicFieldsOrder.indexOf(actualStartField);
      lastColumnIndex = Math.min(startFieldIndex + maxColumns - 1, basicFieldsOrder.length - 1);
      lastField = basicFieldsOrder[lastColumnIndex] || actualStartField;
    }
    
    setSelectedCell({ row: lastRowIndex, field: lastField });
    
    // Przenieś focus na ostatnią wklejoną komórkę
    setTimeout(() => {
      const lastInputId = (isExtendedField || isQuantityField)
        ? `input-${cardIndex}-${lastRowIndex}-${lastField}`
        : `excel-input-${lastRowIndex}-${lastField}`;
      const lastInput = document.getElementById(lastInputId);
      if (lastInput) {
        lastInput.focus();
      }
    }, 0);
  };

  // Funkcja do czyszczenia komórki
  const clearCell = (rowIndex, field) => {
    const updatedCards = cardsData.map(card => ({
      ...card,
      data: card.data.map((r, idx) => {
        if (idx === rowIndex) {
          const updated = { ...r };
          if (field === 'check') updated.check = '';
          else if (field === 'j') updated.j = '';
          else if (field === 'seq') updated.seq = '';
          else if (field === 'opis') updated.opis = '';
          return updated;
        }
        return r;
      })
    }));
    setCardsData(updatedCards);
    updatedCards.forEach((card, index) => {
      saveCard(card, index, true);
    });
  };

  // Funkcja do obsługi nawigacji klawiszami w tabeli Excel
  const handleExcelKeyDown = (e, rowIndex, field) => {
    const totalRows = cardsData[0]?.data.length || 0;
    const fields = ['check', 'j', 'seq', 'opis'];
    const currentFieldIndex = fields.indexOf(field);
    
    // Obsługa Delete i Backspace - usuń zawartość komórki (jak w Excelu)
    if (e.key === 'Delete' || e.key === 'Backspace') {
      const input = e.target;
      const hasSelection = input.selectionStart !== input.selectionEnd;
      
      // Jeśli cała zawartość jest zaznaczona lub komórka jest pusta, usuń całą zawartość
      if (hasSelection && input.selectionStart === 0 && input.selectionEnd === input.value.length) {
        e.preventDefault();
        clearCell(rowIndex, field);
        return;
      }
      
      // Jeśli kursor jest na końcu i nic nie jest zaznaczone, usuń całą zawartość (jak w Excelu)
      if (!hasSelection && (input.selectionStart === input.value.length || input.value === '')) {
        e.preventDefault();
        clearCell(rowIndex, field);
        return;
      }
      
      // W przeciwnym razie pozwól na normalne usuwanie znaków (domyślne zachowanie)
      return;
    }
    
    // Obsługa strzałek - nawigacja między komórkami
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
      e.preventDefault();
      
      let newRowIndex = rowIndex;
      let newFieldIndex = currentFieldIndex;
      
      switch (e.key) {
        case 'ArrowUp':
          newRowIndex = Math.max(0, rowIndex - 1);
          break;
        case 'ArrowDown':
          newRowIndex = Math.min(totalRows - 1, rowIndex + 1);
          break;
        case 'ArrowLeft':
          if (newFieldIndex > 0) {
            newFieldIndex--;
          } else if (rowIndex > 0) {
            // Przejdź do ostatniej kolumny poprzedniego wiersza
            newRowIndex = rowIndex - 1;
            newFieldIndex = fields.length - 1;
          }
          break;
        case 'ArrowRight':
          if (newFieldIndex < fields.length - 1) {
            newFieldIndex++;
          } else if (rowIndex < totalRows - 1) {
            // Przejdź do pierwszej kolumny następnego wiersza
            newRowIndex = rowIndex + 1;
            newFieldIndex = 0;
          }
          break;
        case 'Tab':
          if (e.shiftKey) {
            // Shift+Tab - w lewo
            if (newFieldIndex > 0) {
              newFieldIndex--;
            } else if (rowIndex > 0) {
              newRowIndex = rowIndex - 1;
              newFieldIndex = fields.length - 1;
            }
          } else {
            // Tab - w prawo
            if (newFieldIndex < fields.length - 1) {
              newFieldIndex++;
            } else if (rowIndex < totalRows - 1) {
              newRowIndex = rowIndex + 1;
              newFieldIndex = 0;
            }
          }
          break;
        case 'Enter':
          // Enter - w dół (jak w Excelu)
          newRowIndex = Math.min(totalRows - 1, rowIndex + 1);
          break;
        default:
          return;
      }
      
      // Przejdź do nowej komórki
      const newField = fields[newFieldIndex];
      const nextInputId = `excel-input-${newRowIndex}-${newField}`;
      const nextInput = document.getElementById(nextInputId);
      if (nextInput) {
        setSelectedCell({ row: newRowIndex, field: newField });
        nextInput.focus();
        if (typeof nextInput.select === 'function') {
          nextInput.select();
        }
      }
    }
  };

  // Usuń pozycję z wszystkich kart
  const handleDeleteRow = async (rowIndex) => {
    // Sprawdź uprawnienie do usuwania wierszy
    if (!hasDashboardPermission(user, 'deleteRows')) {
      alert('Nie masz uprawnień do usuwania wierszy.');
      return;
    }
    
    const updatedCards = cardsData.map(card => ({
      ...card,
      data: card.data.filter((_, index) => index !== rowIndex)
    }));
    
    console.log('🗑️ Usuwanie wiersza:', {
      rowIndex,
      przedUsunieciem: cardsData[0]?.data?.length || 0,
      poUsunieciu: updatedCards[0]?.data?.length || 0,
      wszystkieSeqsPrzed: cardsData[0]?.data?.map(r => r.seq) || [],
      wszystkieSeqsPo: updatedCards[0]?.data?.map(r => r.seq) || []
    }); // Debug
    
    // Synchronizuj liczbę wierszy (wszystkie karty powinny mieć taką samą liczbę wierszy)
    const synchronizedCards = synchronizeRows(updatedCards);
    
    console.log('🔄 Po synchronizacji:', {
      wszystkieSeqs: synchronizedCards[0]?.data?.map(r => r.seq) || [],
      dataLength: synchronizedCards[0]?.data?.length || 0
    }); // Debug
    
    setCardsData(synchronizedCards);
    
    // Zapisz wszystkie karty NATYCHMIAST (bez debouncing) - ważne dla operacji usuwania
    for (let i = 0; i < synchronizedCards.length; i++) {
      await saveCard(synchronizedCards[i], i, true); // immediate = true
    }
    
    console.log('✅ Wszystkie karty zapisane po usunięciu wiersza'); // Debug
  };

  // Funkcje do zarządzania przypomnieniami
  const handleAddReminder = async () => {
    if (!reminderForm.title.trim()) {
      alert('Wprowadź tytuł przypomnienia');
      return;
    }

    if (reminderForm.isRecurring) {
      if (!reminderForm.recurringDays || reminderForm.recurringDays.length === 0 || !reminderForm.recurringTime) {
        alert('Wybierz przynajmniej jeden dzień tygodnia i godzinę dla przypomnienia powtarzającego się');
        return;
      }
    } else {
      if (!reminderForm.date || !reminderForm.time) {
        alert('Wprowadź datę i godzinę dla przypomnienia jednorazowego');
        return;
      }
    }

    const project = getCurrentProject();
    if (!project) {
      alert('Brak przypisanego projektu');
      return;
    }

    try {
      const newReminder = await remindersAPI.create({
        project,
        title: reminderForm.title,
        description: reminderForm.description,
        date: reminderForm.date,
        time: reminderForm.time,
        isRecurring: reminderForm.isRecurring,
        recurringDays: reminderForm.recurringDays || [],
        recurringTime: reminderForm.recurringTime,
        assignedTo: reminderForm.assignedTo === 'all' ? null : parseInt(reminderForm.assignedTo)
      });

      setReminders([...reminders, newReminder]);
      setReminderForm({
        title: '',
        description: '',
        date: '',
        time: '',
        isRecurring: false,
        recurringDays: [],
        recurringTime: '',
        assignedTo: 'all' // Reset do domyślnej wartości
      });
      
      // Pokaż komunikat sukcesu w modalu
      const reminderInfo = newReminder.isRecurring
        ? `powtarzające się (${(newReminder.recurringDays || []).map(d => {
            const days = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];
            return days[d];
          }).join(', ')} o ${newReminder.recurringTime})`
        : `jednorazowe (${newReminder.date} o ${newReminder.time})`;
      
      // Określ do kogo jest przypomnienie
      let assignedToInfo = 'Wszyscy użytkownicy';
      if (newReminder.assignedTo) {
        const assignedUser = users.find(u => u.id === newReminder.assignedTo);
        if (assignedUser) {
          assignedToInfo = assignedUser.name || assignedUser.email;
        }
      }
      
      setReminderSuccessMessage({
        title: newReminder.title,
        info: reminderInfo,
        assignedTo: assignedToInfo
      });
      
      // Automatycznie ukryj komunikat po 5 sekundach
      setTimeout(() => {
        setReminderSuccessMessage(null);
      }, 5000);
      
      // Sprawdź od razu czy nowe przypomnienie powinno się pokazać
      // (jeśli użytkownik ustawił przypomnienie na aktualną godzinę)
      setTimeout(() => {
        // Sprawdź czy przypomnienie jest dla tego użytkownika (lub dla wszystkich)
        if (newReminder.assignedTo !== null && newReminder.assignedTo !== undefined && newReminder.assignedTo !== user?.id) {
          return; // Pomiń jeśli przypomnienie jest dla innego użytkownika
        }
        
        const now = new Date();
        const currentDate = getTodayDate();
        const nowTimestamp = now.getTime();
        const toTimestamp = (dateStr, timeStr) => {
          if (!dateStr || !timeStr) return null;
          return new Date(`${dateStr}T${timeStr}`).getTime();
        };
        
        const enqueueReminder = (reminder, lastShownKey) => {
          if (localStorage.getItem(lastShownKey)) return;
          localStorage.setItem(lastShownKey, 'true');
          setReminderQueue(prev => {
            if (activeReminderPopup?.id === reminder.id || prev.some(r => r.id === reminder.id)) {
              return prev;
            }
            return [...prev, reminder];
          });
        };

        if (newReminder.isRecurring) {
          const currentDay = now.getDay();
          const reminderDays = newReminder.recurringDays || [];
          const recurringTimestamp = toTimestamp(currentDate, newReminder.recurringTime);
          if (reminderDays.includes(currentDay) && recurringTimestamp !== null && nowTimestamp >= recurringTimestamp) {
            const lastShownKey = `reminder_${newReminder.id}_${currentDate}_recurring_${newReminder.recurringTime}`;
            enqueueReminder(newReminder, lastShownKey);
          }
        } else {
          const reminderTimestamp = toTimestamp(newReminder.date, newReminder.time);
          if (newReminder.date === currentDate && reminderTimestamp !== null && nowTimestamp >= reminderTimestamp) {
            const lastShownKey = `reminder_${newReminder.id}_${currentDate}_once`;
            enqueueReminder(newReminder, lastShownKey);
          }
        }
      }, 500);
      
      // Odśwież listę przypomnień, aby nowo dodane przypomnienie było widoczne
      // (to jest szczególnie ważne jeśli przypomnienie jest dla innego użytkownika)
      setTimeout(async () => {
        const project = getCurrentProject();
        if (project && authAPI.isAuthenticated()) {
          try {
            const loadedReminders = await remindersAPI.getByProject(project);
            setReminders(loadedReminders || []);
          } catch (error) {
            console.error('Błąd odświeżania przypomnień po dodaniu:', error);
          }
        }
      }, 1000);
    } catch (error) {
      console.error('Błąd tworzenia przypomnienia:', error);
      alert(`Nie udało się utworzyć przypomnienia: ${error.message || error}`);
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    if (window.confirm('Czy na pewno chcesz usunąć to przypomnienie?')) {
      try {
        await remindersAPI.delete(reminderId);
        setReminders(reminders.filter(r => r.id !== reminderId));
      } catch (error) {
        console.error('Błąd usuwania przypomnienia:', error);
        alert('Nie udało się usunąć przypomnienia');
      }
    }
  };

  const handleToggleReminderSelection = (reminderId) => {
    setSelectedRemindersToDelete(prev => {
      if (prev.includes(reminderId)) {
        return prev.filter(id => id !== reminderId);
      } else {
        return [...prev, reminderId];
      }
    });
  };

  const handleDeleteSelectedReminders = () => {
    if (selectedRemindersToDelete.length === 0) {
      alert('Nie zaznaczono żadnych przypomnień do usunięcia');
      return;
    }

    // Pokaż modal potwierdzenia zamiast window.confirm
    setDeleteConfirmData({
      count: selectedRemindersToDelete.length,
      callback: async () => {
        try {
          console.log('🗑️ Frontend: Usuwanie przypomnień, IDs:', selectedRemindersToDelete);
          console.log('🗑️ Frontend: Typ IDs:', typeof selectedRemindersToDelete[0]);
          console.log('🗑️ Frontend: IDs to tablica?', Array.isArray(selectedRemindersToDelete));
          
          await remindersAPI.deleteMany(selectedRemindersToDelete);
          setReminders(reminders.filter(r => !selectedRemindersToDelete.includes(r.id)));
          setSelectedRemindersToDelete([]);
          setShowDeleteConfirmModal(false);
        } catch (error) {
          console.error('❌ Frontend: Błąd usuwania przypomnień:', error);
          console.error('❌ Frontend: Szczegóły błędu:', error.message);
          alert('Nie udało się usunąć przypomnień: ' + (error.message || 'Nieznany błąd'));
        }
      }
    });
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmData.callback) {
      deleteConfirmData.callback();
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmModal(false);
    setDeleteConfirmData({ count: 0, callback: null });
  };

  const handleCloseReminderPopup = () => {
    setActiveReminderPopup(null);
  };

  // Zamknij popup powiadomienia jakościowego: oznacz jako przeczytane w API, usuń z listy, pokaż następne
  const handleCloseQualityAlertPopup = async (markAsRead = true) => {
    const current = activeQualityAlertPopup;
    if (!current) return;
    if (markAsRead) {
      try {
        await apiRequest(`/quality/notifications/${current.id}/read`, { method: 'PUT' });
      } catch (e) {
        console.warn('Oznaczanie powiadomienia jako przeczytane:', e?.message || e);
      }
    }
    const rest = qualityAlertNotifications.filter(n => n.id !== current.id);
    setQualityAlertNotifications(rest);
    setActiveQualityAlertPopup(rest.length > 0 ? rest[0] : null);
  };

  const handleConfirmReminder = async () => {
    if (!activeReminderPopup) return;

    const project = getCurrentProject();
    if (!project) return;

    // Zamknij popup (bez zapisywania historii)
    setActiveReminderPopup(null);
  };

  const handlePostponeReminder = async () => {
    if (!activeReminderPopup) return;

    const project = getCurrentProject();
    if (!project) return;

    // Oblicz nowy czas przypomnienia
    const now = new Date();
    const newTime = new Date(now.getTime() + postponeMinutes * 60000);
    const newTimeString = `${String(newTime.getHours()).padStart(2, '0')}:${String(newTime.getMinutes()).padStart(2, '0')}`;

    // Zaktualizuj przypomnienie (tylko lokalnie, bo przypomnienia są w bazie)
    const updatedReminders = reminders.map(r => {
      if (r.id === activeReminderPopup.id) {
        if (r.isRecurring) {
          // Dla przypomnień powtarzających się, zmień tylko godzinę na dzisiaj
          return { ...r, postponedUntil: newTimeString };
        } else {
          // Dla przypomnień jednorazowych, zmień datę i godzinę
          const newDate = newTime.toISOString().split('T')[0];
          return { ...r, date: newDate, time: newTimeString, postponedUntil: newTimeString };
        }
      }
      return r;
    });

    setReminders(updatedReminders);

    // Zamknij popup (bez zapisywania historii)
    setActiveReminderPopup(null);
  };

  // Funkcja do wyzerowania wszystkich aktywnych pól w karcie
  const handleResetCard = (cardIndex) => {
    // Sprawdź, czy użytkownik ma rolę Owner
    if (user?.role !== 'owner') {
      return; // Dla użytkowników bez roli Owner nic się nie dzieje
    }
    
    // Zapisz poprzedni stan na wypadek błędu
    const previousCards = [...cardsData];
    
    const updatedCards = cardsData.map((card, index) => {
      if (index === cardIndex) {
        const resetCard = {
          ...card,
          date: '', // Wyzeruj datę (pusty string)
          time: '', // Wyzeruj czas (pusty string)
          trailerId: '', // Wyzeruj numer trailera (pusty string)
          code: 'PL', // Resetuj status do domyślnego
          lastUpdateTime: '', // Wyczyść czas ostatniej aktualizacji
          data: card.data.map(row => ({
            check: normalizeCheckField(row.check),
            seq: row.seq,
            j: row.j,
            opis: row.opis || '',
            doZrobienia: null,
            zrobione: null,
            przypisanie: null,
            assignedTo: null,
            komentarz: null
          }))
        };
        
        // Najpierw zaktualizuj stan lokalny
        setCardsData(prevCards => {
          const newCards = [...prevCards];
          newCards[index] = resetCard;
          return newCards;
        });
        
        // Potem zapisz do backendu
        const project = getCurrentProject();
        if (project && authAPI.isAuthenticated()) {
          const cardToSave = {
            project: project,
            cardNumber: resetCard.cardNumber,
            trailerId: '', // Pusty string zamiast null
            date: '', // Pusty string zamiast null
            time: '', // Pusty string zamiast null
            code: 'PL', // Domyślna wartość zamiast null
            lastUpdateTime: '', // Wyczyść czas ostatniej aktualizacji
            data: resetCard.data.map(row => ({
              check: normalizeCheckField(row.check),
              seq: row.seq,
              j: row.j,
              opis: row.opis || '',
              doZrobienia: null,
              zrobione: null,
              przypisanie: null,
              assignedTo: null,
              komentarz: null,
              ocz: '',
              up: '',
              upok: '',
              av: '',
              sk: '',
              er: ''
            }))
          };
          
          // Zapisz synchronicznie
          dashboardAPI.upsert(cardToSave)
            .then(() => {
              console.log('Karta wyzerowana i zapisana:', cardToSave);
              // Nie odświeżaj automatycznie - dane są już zaktualizowane w stanie
            })
            .catch(error => {
              console.error('Błąd zapisywania wyzerowanej karty:', error);
              // W przypadku błędu, przywróć poprzednie dane
              setCardsData(previousCards);
            });
        }
        
        return resetCard;
      }
      return card;
    });
  };

  // Opóźnione pokazanie błędu - daj czas na załadowanie projektów
  useEffect(() => {
    if (user && !getCurrentProject()) {
      const timer = setTimeout(() => {
        // Sprawdź ponownie po 2 sekundach
        const project = getCurrentProject();
        if (!project) {
          setShowProjectError(true);
        }
      }, 2000);
      
      return () => clearTimeout(timer);
    } else {
      setShowProjectError(false);
    }
  }, [user, projects]);

  // Cleanup timeout przy unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
  return (
    <div className="dashboard">
        <div style={{ padding: '20px', textAlign: 'center' }}>Ładowanie danych...</div>
      </div>
    );
  }

  // Sprawdź, czy użytkownik jest jeszcze ładowany
  // Jeśli user jest null/undefined, oznacza to, że dane jeszcze się ładują
  const userStillLoading = !user;
  
  // Jeśli użytkownik jeszcze się ładuje lub dane są ładowane, pokaż komunikat ładowania
  if (userStillLoading || loading) {
    return (
      <div className="dashboard">
        <div style={{ padding: '20px', textAlign: 'center' }}>Ładowanie danych...</div>
          </div>
    );
  }
  
  const currentProject = getCurrentProject();
  
  // Jeśli użytkownik jest załadowany, ale brak projektu
  if (!currentProject && user) {
    // Sprawdź, czy projekty mogą się jeszcze ładować
    // Jeśli projects jest pustą tablicą [], ale user.projects ma dane, to projekty jeszcze się ładują
    const userHasProjects = user.projects && Array.isArray(user.projects) && user.projects.length > 0;
    const projectsIsEmpty = Array.isArray(projects) && projects.length === 0;
    
    // Jeśli użytkownik ma przypisane projekty (user.projects), ale projects (props) jest pustą tablicą,
    // oznacza to, że projekty jeszcze się ładują - pokaż komunikat ładowania
    if (userHasProjects && projectsIsEmpty) {
      return (
        <div className="dashboard">
          <div style={{ padding: '20px', textAlign: 'center' }}>Ładowanie danych...</div>
        </div>
      );
    }
    
    // Jeśli projects (props) ma dane, ale getCurrentProject() zwraca null,
    // oznacza to, że użytkownik nie ma dostępu do żadnego projektu - pokaż błąd
    // Ale tylko po opóźnieniu (showProjectError), aby dać czas na załadowanie projektów
    if (!projectsIsEmpty && showProjectError) {
      return (
        <div className="dashboard">
          <div style={{ padding: '20px', textAlign: 'center' }}>
            Brak przypisanego projektu. Skontaktuj się z administratorem.
          </div>
        </div>
      );
    }
    
    // W przeciwnym razie pokaż komunikat ładowania (projekty mogą się jeszcze ładować)
    return (
      <div className="dashboard">
        <div style={{ padding: '20px', textAlign: 'center' }}>Ładowanie danych...</div>
          </div>
    );
  }

  // Sprawdź uprawnienie "Przeglądanie" - jeśli użytkownik nie ma tego uprawnienia, pokaż komunikat
  if (!hasDashboardPermission(user, 'view')) {
    return (
      <div className="dashboard">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <h2>Brak dostępu</h2>
          <p>Nie masz uprawnień do przeglądania Dashboard. Skontaktuj się z administratorem.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard" onMouseMove={handleUserIconMouseMove} onMouseUp={handleUserIconMouseUp}>
      {/* Przycisk toggle - zawsze widoczny */}
      <button 
        className={`toolbar-toggle-btn-float ${isToolbarVisible ? 'toolbar-visible' : 'toolbar-hidden'}`}
        onClick={() => setIsToolbarVisible(!isToolbarVisible)}
        title={isToolbarVisible ? "Ukryj toolbar" : "Pokaż toolbar"}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          {isToolbarVisible ? (
            <path d="M18 6L6 18M6 6l12 12"/>
          ) : (
            <path d="M3 12h18M3 6h18M3 18h18"/>
          )}
        </svg>
      </button>
      
      {/* Toolbar z lewej strony */}
      <div 
        className={`dashboard-toolbar ${isToolbarVisible ? 'visible' : 'hidden'}`}
        onMouseLeave={() => {
          // Automatycznie ukryj toolbar natychmiast po opuszczeniu obszaru
          if (isToolbarVisible) {
            setIsToolbarVisible(false);
          }
        }}
      >
        {hasDashboardPermission(user, 'toolbar.showAssignment') && (
          <button 
            className="toolbar-icon"
            onClick={(e) => {
              // Sprawdź uprawnienie przed otwarciem modala przypisywania
              if (!hasDashboardPermission(user, 'edit')) {
                alert('Nie masz uprawnień do przypisywania użytkowników.');
                return;
              }
              setSelectedShiftInModal('A'); // Reset do zmiany A przy otwieraniu modala
              setShowAssignmentModal(true);
            }}
            title="Przypisz użytkownika"
            style={{ marginTop: '0' }}
            disabled={!hasDashboardPermission(user, 'edit')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </button>
        )}
        {hasDashboardPermission(user, 'toolbar.showHistory') && (
          <button 
            className="toolbar-icon"
            onClick={() => {
              // Sprawdź uprawnienie przed otwarciem modala historii
              if (!hasDashboardPermission(user, 'viewHistory')) {
                alert('Nie masz uprawnień do przeglądania historii.');
                return;
              }
              setShowHistoryModal(true);
            }}
            title="Historia"
            disabled={!hasDashboardPermission(user, 'viewHistory')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </button>
        )}
        {hasDashboardPermission(user, 'toolbar.showReminders') && (
          <button 
            className="toolbar-icon"
            onClick={() => {
              // Sprawdź uprawnienie przed otwarciem modala przypomnień
              if (!hasDashboardPermission(user, 'addReminders') && !hasDashboardPermission(user, 'deleteReminders')) {
                alert('Nie masz uprawnień do zarządzania przypomnieniami.');
                return;
              }
              setShowReminderModal(true);
            }}
            title="Przypomnienie"
            disabled={!hasDashboardPermission(user, 'addReminders') && !hasDashboardPermission(user, 'deleteReminders')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
          </button>
        )}
        <button
          className="toolbar-icon"
          onClick={() => setShowZoomModal(true)}
          title="Skala dashboardu"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <circle cx="11" cy="11" r="7"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            <line x1="11" y1="8" x2="11" y2="14"/>
            <line x1="8" y1="11" x2="14" y2="11"/>
          </svg>
        </button>
        {hasDashboardPermission(user, 'toolbar.showSeqSettings') && (
          <button 
            className="toolbar-icon"
            onClick={() => {
              // Sprawdź uprawnienie przed otwarciem modala zarządzania SEQ
              if (!hasDashboardPermission(user, 'manageSeq')) {
                alert('Nie masz uprawnień do zarządzania pozycjami SEQ.');
                return;
              }
              setShowSettingsModal(true);
            }}
            title="Zarządzaj pozycjami SEQ"
            style={{ marginTop: 'auto' }}
            disabled={!hasDashboardPermission(user, 'manageSeq')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24"/>
            </svg>
          </button>
        )}
        {hasDashboardPermission(user, 'toolbar.showFullscreen') && (
          <button 
            className="toolbar-icon"
            onClick={toggleFullscreen}
            title={isFullscreen ? "Wyjdź z pełnego ekranu" : "Pełny ekran"}
            style={{ marginTop: 'auto' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              {isFullscreen ? (
                <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/>
              ) : (
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
              )}
            </svg>
          </button>
        )}
          </div>
        
      <div
        className={`dashboard-content ${isToolbarVisible ? 'toolbar-visible' : 'toolbar-hidden'} ${isFullscreen ? 'fullscreen-mode' : ''}`}
        style={{ zoom: `${dashboardZoom}%` }}
      >
      {saving && (
        <div style={{ 
          position: 'fixed', 
          top: '10px', 
          right: '10px', 
          background: '#4CAF50', 
          color: 'white', 
          padding: '10px 20px', 
          borderRadius: '5px',
          zIndex: 1000
        }}>
          Zapisuję...
        </div>
      )}

      {/* Modal do zarządzania pozycjami */}
      {showSettingsModal && (
        <div className="modal-overlay" onClick={() => setShowSettingsModal(false)}>
          <div className="modal-content history-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Zarządzanie pozycjami SEQ</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowSettingsModal(false)}
              >
                ×
              </button>
        </div>
            <div className="modal-body history-modal-body">
              <div className="seq-excel-table-container">
                <div className="seq-excel-header">
                  <h4>Zarządzanie pozycjami (format Excel)</h4>
                  <div className="seq-excel-actions">
                    <button
                      className="seq-add-row-btn"
                      onClick={() => {
                        handleAddRow();
                      }}
                      title="Dodaj wiersz"
                      disabled={!hasDashboardPermission(user, 'addRows')}
                    >
                      + Wiersz
                    </button>
                    <button
                      className="seq-remove-row-btn"
                      onClick={() => {
                        if (cardsData[0]?.data.length > 0) {
                          handleDeleteRow(cardsData[0].data.length - 1);
                        }
                      }}
                      title="Usuń ostatni wiersz"
                      disabled={!cardsData[0]?.data || cardsData[0].data.length === 0 || !hasDashboardPermission(user, 'deleteRows')}
                    >
                      - Wiersz
                    </button>
          </div>
        </div>
        
                <div className="seq-excel-table-wrapper">
                  <table className="seq-excel-table">
                    <thead>
                      <tr>
                        <th className="seq-excel-th seq-excel-th-check">Check</th>
                        <th className="seq-excel-th seq-excel-th-j">J</th>
                        <th className="seq-excel-th seq-excel-th-seq">SEQ</th>
                        <th className="seq-excel-th seq-excel-th-opis">Opis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cardsData[0]?.data.map((row, rowIndex) => (
                        <tr key={rowIndex} className="seq-excel-row">
                          <td
                            className={`seq-excel-cell seq-excel-cell-check ${selectedCell.row === rowIndex && selectedCell.field === 'check' ? 'seq-excel-cell-selected' : ''}`}
                          >
                            <input
                              type="text"
                              inputMode="numeric"
                              autoComplete="off"
                              maxLength={2}
                              id={`excel-input-${rowIndex}-check`}
                              title="Liczba od 1 do 10 (pole może być puste)"
                              className={`seq-excel-input ${selectedCell.row === rowIndex && selectedCell.field === 'check' ? 'selected' : ''}`}
                              value={normalizeCheckField(row.check)}
                              onChange={(e) => {
                                if (!hasDashboardPermission(user, 'edit')) {
                                  alert('Nie masz uprawnień do edycji Dashboard.');
                                  return;
                                }
                                const raw = e.target.value.replace(/\D/g, '');
                                let next = '';
                                if (raw !== '') {
                                  const n = parseInt(raw, 10);
                                  if (Number.isNaN(n)) return;
                                  if (n < 1) next = '';
                                  else next = String(Math.min(10, n));
                                }
                                const updatedCards = cardsData.map(card => ({
                                  ...card,
                                  data: card.data.map((r, idx) =>
                                    idx === rowIndex ? { ...r, check: next } : r
                                  )
                                }));
                                setCardsData(updatedCards);
                                updatedCards.forEach((card, index) => {
                                  saveCard(card, index, true);
                                });
                              }}
                              disabled={!hasDashboardPermission(user, 'edit')}
                              onFocus={() => setSelectedCell({ row: rowIndex, field: 'check' })}
                              onKeyDown={(e) => handleExcelKeyDown(e, rowIndex, 'check')}
                              onPaste={(e) => {
                                if (!hasDashboardPermission(user, 'edit')) {
                                  e.preventDefault();
                                  alert('Nie masz uprawnień do edycji Dashboard.');
                                  return;
                                }
                                e.preventDefault();
                                handlePaste(e, rowIndex, 'check', selectedCell);
                              }}
                              placeholder=""
                            />
                          </td>
                          <td className="seq-excel-cell seq-excel-cell-j">
                            <input
                              type="text"
                              id={`excel-input-${rowIndex}-j`}
                              className={`seq-excel-input ${selectedCell.row === rowIndex && selectedCell.field === 'j' ? 'selected' : ''}`}
                              value={row.j || ''}
                              onChange={(e) => {
                                if (!hasDashboardPermission(user, 'edit')) {
                                  alert('Nie masz uprawnień do edycji Dashboard.');
                                  return;
                                }
                                const updatedCards = cardsData.map(card => ({
                                  ...card,
                                  data: card.data.map((r, idx) => 
                                    idx === rowIndex ? { ...r, j: e.target.value } : r
                                  )
                                }));
                                setCardsData(updatedCards);
                                updatedCards.forEach((card, index) => {
                                  saveCard(card, index, true);
                                });
                              }}
                              disabled={!hasDashboardPermission(user, 'edit')}
                              onFocus={() => setSelectedCell({ row: rowIndex, field: 'j' })}
                              onKeyDown={(e) => handleExcelKeyDown(e, rowIndex, 'j')}
                              onPaste={(e) => {
                                if (!hasDashboardPermission(user, 'edit')) {
                                  e.preventDefault();
                                  alert('Nie masz uprawnień do edycji Dashboard.');
                                  return;
                                }
                                e.preventDefault();
                                handlePaste(e, rowIndex, 'j', selectedCell);
                              }}
                              placeholder=""
                            />
                          </td>
                          <td className="seq-excel-cell seq-excel-cell-seq">
                            <input
                              type="text"
                              id={`excel-input-${rowIndex}-seq`}
                              className={`seq-excel-input ${selectedCell.row === rowIndex && selectedCell.field === 'seq' ? 'selected' : ''}`}
                              value={row.seq || ''}
                              onChange={(e) => {
                                if (!hasDashboardPermission(user, 'edit')) {
                                  alert('Nie masz uprawnień do edycji Dashboard.');
                                  return;
                                }
                                const updatedCards = cardsData.map(card => ({
                                  ...card,
                                  data: card.data.map((r, idx) => 
                                    idx === rowIndex ? { ...r, seq: e.target.value } : r
                                  )
                                }));
                                setCardsData(updatedCards);
                                updatedCards.forEach((card, index) => {
                                  saveCard(card, index, true);
                                });
                              }}
                              disabled={!hasDashboardPermission(user, 'edit')}
                              onFocus={() => setSelectedCell({ row: rowIndex, field: 'seq' })}
                              onKeyDown={(e) => handleExcelKeyDown(e, rowIndex, 'seq')}
                              onPaste={(e) => {
                                if (!hasDashboardPermission(user, 'edit')) {
                                  e.preventDefault();
                                  alert('Nie masz uprawnień do edycji Dashboard.');
                                  return;
                                }
                                e.preventDefault();
                                handlePaste(e, rowIndex, 'seq', selectedCell);
                              }}
                              placeholder=""
                            />
                          </td>
                          <td className="seq-excel-cell seq-excel-cell-opis">
                            <input
                              type="text"
                              id={`excel-input-${rowIndex}-opis`}
                              className={`seq-excel-input ${selectedCell.row === rowIndex && selectedCell.field === 'opis' ? 'selected' : ''}`}
                              value={row.opis || ''}
                              onChange={(e) => {
                                if (!hasDashboardPermission(user, 'edit')) {
                                  alert('Nie masz uprawnień do edycji Dashboard.');
                                  return;
                                }
                                const updatedCards = cardsData.map(card => ({
                                  ...card,
                                  data: card.data.map((r, idx) => 
                                    idx === rowIndex ? { ...r, opis: e.target.value } : r
                                  )
                                }));
                                setCardsData(updatedCards);
                                updatedCards.forEach((card, index) => {
                                  saveCard(card, index, true);
                                });
                              }}
                              disabled={!hasDashboardPermission(user, 'edit')}
                              onFocus={() => setSelectedCell({ row: rowIndex, field: 'opis' })}
                              onKeyDown={(e) => handleExcelKeyDown(e, rowIndex, 'opis')}
                              onPaste={(e) => {
                                if (!hasDashboardPermission(user, 'edit')) {
                                  e.preventDefault();
                                  alert('Nie masz uprawnień do edycji Dashboard.');
                                  return;
                                }
                                e.preventDefault();
                                handlePaste(e, rowIndex, 'opis', selectedCell);
                              }}
                              placeholder=""
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showZoomModal && (
        <div className="modal-overlay" onClick={() => setShowZoomModal(false)}>
          <div className="modal-content history-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Skala dashboardu</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowZoomModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body history-modal-body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center' }}>
                <button
                  className="history-send-btn"
                  onClick={() => updateDashboardZoom(dashboardZoom - 10)}
                  disabled={dashboardZoom <= 50}
                >
                  -
                </button>
                <span style={{ minWidth: '80px', textAlign: 'center', fontWeight: 700 }}>
                  {dashboardZoom}%
                </span>
                <button
                  className="history-send-btn"
                  onClick={() => updateDashboardZoom(dashboardZoom + 10)}
                  disabled={dashboardZoom >= 150}
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal do przypisywania użytkowników */}
      {showAssignmentModal && (
        <div 
          className="modal-overlay assignment-modal-overlay"
          onClick={(e) => {
            // Zamykaj modal tylko jeśli kliknięto bezpośrednio w overlay, nie w modal content
            if (e.target === e.currentTarget) {
              setShowAssignmentModal(false);
            }
          }}
          onDragOver={(e) => {
            // Pozwól na drag and drop przez overlay
            e.preventDefault();
          }}
        >
          <div 
            className="modal-content assignment-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              left: modalPosition.x || '50%',
              top: modalPosition.y || '50%',
              transform: modalPosition.x ? 'none' : 'translate(-50%, -50%)',
              cursor: isDraggingModal ? 'grabbing' : 'default'
            }}
          >
            <div 
              className="modal-header assignment-modal-header" 
              style={{ cursor: 'grab' }}
              onMouseDown={(e) => {
                // Tylko nagłówek może przesuwać modal, i tylko jeśli nie ma aktywnego drag
                if (!e.target.closest('.modal-close-btn') && !draggedUser) {
                  handleModalMouseDown(e);
                }
              }}
            >
              <h3>Przypisz użytkownika</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowAssignmentModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body assignment-modal-body">
              {/* Przełącznik zmiany w modalu */}
              <div className="shift-selector-modal" style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '10px', 
                marginBottom: '20px',
                padding: '10px'
              }}>
                {['A', 'B', 'C'].map((shift) => (
                  <button
                    key={shift}
                    onClick={() => setSelectedShiftInModal(shift)}
                    style={{
                      padding: '10px 20px',
                      fontSize: '16px',
                      fontWeight: '500',
                      border: '2px solid',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      backgroundColor: selectedShiftInModal === shift ? '#667eea' : 'white',
                      color: selectedShiftInModal === shift ? 'white' : '#667eea',
                      borderColor: '#667eea'
                    }}
                  >
                    {shift}
                  </button>
                ))}
              </div>

              <div className="users-list">
                {(() => {
                  const filteredEmployees = getFilteredEmployees();
                  if (filteredEmployees.length === 0) {
                    return (
                      <div className="no-users-message">
                        Brak pracowników z projektu MAN, stanowiskiem SEQ i zmianą {selectedShiftInModal}
                      </div>
                    );
                  }
                  
                  const assignedUsers = getAssignedUsers();
                  return filteredEmployees.map((userItem, userIndex) => {
                    const userName = userItem.name || `${userItem.firstName || ''} ${userItem.lastName || ''}`.trim() || `Pracownik ${userIndex + 1}`;
                    const isAssigned = assignedUsers.has(userName);
                    const displayName = userItem.firstName && userItem.lastName 
                      ? `${userItem.firstName} ${userItem.lastName}` 
                      : userName;
                    
                    return (
                      <div
                        key={userItem.id || userIndex}
                        className={`user-item ${isAssigned ? 'user-assigned' : ''}`}
                        draggable={!isAssigned}
                        onDragStart={(e) => {
                          if (!isAssigned) {
                            handleDragStart(e, userName);
                          } else {
                            e.preventDefault();
                          }
                        }}
                        onDragEnd={handleDragEnd}
                        title={isAssigned ? 'Użytkownik jest już przypisany' : 'Przeciągnij, aby przypisać'}
                      >
                        👤 {displayName}
                        {userItem.shift && (
                          <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                            (Zmiana {userItem.shift})
                          </span>
                        )}
                        {isAssigned && <span className="assigned-badge">✓ Przypisany</span>}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Historia */}
      {showHistoryModal && (
        <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Historia</h3>
              <button
                className="modal-close-btn"
                onClick={() => {
                  setShowHistoryModal(false);
                  setHistoryModalTab('view');
                  setSelectedHistoryEntries([]);
                  setHistoryClearMessage(null);
                  setHistoryTrailerSearch('');
                }}
              >
                ×
              </button>
            </div>
            
            {/* Karty/Zakładki */}
            <div className="reminder-modal-tabs">
              <button
                className={`reminder-tab ${historyModalTab === 'view' ? 'active' : ''}`}
                onClick={() => {
                  setHistoryModalTab('view');
                  setSelectedHistoryEntries([]);
                  setHistoryClearMessage(null);
                }}
              >
                Przeglądaj historię
              </button>
              {hasDashboardPermission(user, 'deleteHistory') && (
                <button
                  className={`reminder-tab ${historyModalTab === 'clear' ? 'active' : ''}`}
                  onClick={() => {
                    setHistoryModalTab('clear');
                    setSelectedHistoryEntries([]);
                    setHistoryClearMessage(null);
                  }}
                >
                  Wyczyść
                </button>
              )}
            </div>

            <div className="modal-body">
              {historyModalTab === 'view' ? (
                /* Karta przeglądania historii */
                <div className="history-content">
                  {/* Pole wyszukiwania trailera */}
                  <div className="history-search-container">
                    <label className="history-search-label">Wyszukaj trailer:</label>
                    <input
                      type="text"
                      className="history-search-input"
                      value={historyTrailerSearch}
                      onChange={(e) => setHistoryTrailerSearch(e.target.value)}
                      placeholder="Wpisz numer trailera..."
                    />
                  </div>
                  
                  <div className="history-list">
                    {dashboardHistory.length === 0 ? (
                      <p className="no-history">Brak historii Dashboard</p>
                    ) : (() => {
                      // Filtruj wpisy według numeru trailera, jeśli jest wyszukiwanie
                      let filteredHistory = dashboardHistory;
                      if (historyTrailerSearch.trim() !== '') {
                        filteredHistory = dashboardHistory.filter(entry => 
                          entry.trailerId && entry.trailerId.toLowerCase().includes(historyTrailerSearch.toLowerCase().trim())
                        );
                      }
                      
                      if (filteredHistory.length === 0) {
                        return <p className="no-history">Brak wyników wyszukiwania</p>;
                      }
                      
                      // Funkcja pomocnicza do formatowania daty na DD.MM.YYYY
                      const formatDate = (dateStr) => {
                        if (!dateStr) return '';
                        // Jeśli data jest w formacie YYYY-MM-DD, przekonwertuj na DD.MM.YYYY
                        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
                          const [year, month, day] = dateStr.split('-');
                          return `${day}.${month}.${year}`;
                        }
                        // Jeśli data jest w formacie "DD.MM.YYYY HH:MM" (sentAt), wyciągnij tylko datę
                        if (dateStr.match(/^\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}$/)) {
                          return dateStr.split(' ')[0]; // Zwróć tylko część daty (DD.MM.YYYY)
                        }
                        // Jeśli data jest już w formacie DD.MM.YYYY lub innym, zwróć jak jest
                        return dateStr;
                      };

                      // Pogrupuj wpisy według trailera (trailerId + dateValue + timeValue)
                      const groupedByTrailer = {};
                      filteredHistory.forEach(entry => {
                        // Utwórz klucz unikalny dla trailera (DATA, CZAS, TRAILER)
                        const trailerKey = `${entry.trailerId || ''}_${entry.dateValue || ''}_${entry.timeValue || ''}`;
                        if (!groupedByTrailer[trailerKey]) {
                          groupedByTrailer[trailerKey] = [];
                        }
                        groupedByTrailer[trailerKey].push(entry);
                      });

                      // Sortuj grupy według najnowszej daty zmiany statusu (najnowsze pierwsze)
                      const sortedTrailerKeys = Object.keys(groupedByTrailer).sort((a, b) => {
                        const entriesA = groupedByTrailer[a];
                        const entriesB = groupedByTrailer[b];
                        // Znajdź najnowszy wpis dla każdego trailera
                        const latestA = entriesA.reduce((latest, entry) => {
                          const dateEntry = new Date(entry.createdAt || entry.date + ' ' + entry.time);
                          const dateLatest = latest ? new Date(latest.createdAt || latest.date + ' ' + latest.time) : new Date(0);
                          return dateEntry > dateLatest ? entry : latest;
                        }, null);
                        const latestB = entriesB.reduce((latest, entry) => {
                          const dateB = new Date(entry.createdAt || entry.date + ' ' + entry.time);
                          const dateLatest = latest ? new Date(latest.createdAt || latest.date + ' ' + latest.time) : new Date(0);
                          return dateB > dateLatest ? entry : latest;
                        }, null);
                        
                        const dateA = latestA ? new Date(latestA.createdAt || latestA.date + ' ' + latestA.time) : new Date(0);
                        const dateB = latestB ? new Date(latestB.createdAt || latestB.date + ' ' + latestB.time) : new Date(0);
                        return dateB - dateA; // Najnowsze pierwsze
                      });

                      return (
                        <div className="history-items">
                          {sortedTrailerKeys.map(trailerKey => {
                            const entries = groupedByTrailer[trailerKey];
                            // Sortuj wpisy według daty utworzenia (najstarsze pierwsze)
                            const sortedEntries = [...entries].sort((a, b) => {
                              const dateA = new Date(a.createdAt || a.date + ' ' + a.time);
                              const dateB = new Date(b.createdAt || b.date + ' ' + b.time);
                              return dateA - dateB;
                            });
                            
                            // Pobierz pierwszy wpis dla danych trailera (DATA, CZAS, TRAILER)
                            const firstEntry = sortedEntries[0];
                            
                            // Znajdź wszystkie wpisy PL -> PA dla tego trailera (może być wiele kart)
                            // Obsługa danych legacy: wpis PL -> TT z uzupełnionym statusToDate traktuj jako PL -> PA (czas = entry.date/time)
                            const plToPaEntries = sortedEntries.filter(e =>
                              (e.statusFrom === 'PL' && e.statusTo === 'PA') ||
                              (e.statusFrom === 'PL' && e.statusTo === 'TT' && e.statusToDate)
                            );
                            
                            // Znajdź wszystkie wpisy PA -> TT dla tego trailera (może być wiele kart)
                            // Obsługa danych legacy: wpis PL -> TT z uzupełnionym statusToDate traktuj jako PA -> TT (czas = statusToDate/time)
                            const paToTtEntries = sortedEntries.filter(e =>
                              (e.statusFrom === 'PA' && e.statusTo === 'TT') ||
                              (e.statusFrom === 'PL' && e.statusTo === 'TT' && e.statusToDate)
                            );
                            
                            // Pokaż tylko jeśli jest przynajmniej jeden wpis PL->PA lub PA->TT
                            if (plToPaEntries.length === 0 && paToTtEntries.length === 0) {
                              return null; // Nie pokazuj trailerów bez wpisów PL->PA lub PA->TT
                            }
                            
                            return (
                              <div key={trailerKey} className="history-item-grouped">
                                {/* Linia 1: DATA, CZAS, TRAILER */}
                                <div className="history-line history-line-1">
                                  <strong>DATA:</strong> {formatDate(firstEntry.dateValue) || 'Brak'} | 
                                  <strong> CZAS:</strong> {firstEntry.timeValue || 'Brak'} | 
                                  <strong> TRAILER:</strong> {firstEntry.trailerId || 'Brak'}
                                </div>
                                
                                {/* Linia 2+: Wszystkie wpisy PL -> PA dla tego trailera */}
                                {plToPaEntries.map((plToPa, idx) => (
                                  <div key={`pl-pa-${idx}`} className="history-line history-line-2">
                                    {plToPa.cardNumber} - {plToPa.statusFrom} → {plToPa.statusTo} ({formatDate(plToPa.date)} {plToPa.time}) przez: {plToPa.userName} {plToPa.userLastName || ''}
                                  </div>
                                ))}
                                
                                {/* Linia 3+: Wszystkie wpisy PA -> TT dla tego trailera */}
                                {paToTtEntries.map((paToTt, idx) => {
                                  const displayStatusFrom = paToTt.statusFrom === 'PL' ? 'PA' : paToTt.statusFrom;
                                  return (
                                  <div key={`pa-tt-${idx}`} className="history-line history-line-3">
                                    {paToTt.cardNumber} - {displayStatusFrom} → {paToTt.statusTo} ({formatDate(paToTt.statusToDate || paToTt.date)} {paToTt.statusToTime || paToTt.time}) przez: {paToTt.userName} {paToTt.userLastName || ''}
                                  </div>
                                  );
                                })}
                                
                                {/* Linia 4: Przycisk Wyślij lub informacja Wysłano (tylko dla ostatniego wpisu PA->TT) */}
                                {paToTtEntries.length > 0 && (
                                  <div className="history-line history-line-4">
                                    {(() => {
                                      // Znajdź najnowszy wpis PA->TT
                                      const latestPaToTt = paToTtEntries.reduce((latest, entry) => {
                                        const dateEntry = new Date(entry.createdAt || entry.date + ' ' + entry.time);
                                        const dateLatest = latest ? new Date(latest.createdAt || latest.date + ' ' + latest.time) : new Date(0);
                                        return dateEntry > dateLatest ? entry : latest;
                                      }, null);
                                      
                                      if (latestPaToTt && latestPaToTt.sentAt) {
                                        // sentAt jest w formacie "DD.MM.YYYY HH:MM"
                                        const sentAtFormatted = latestPaToTt.sentAt;
                                        return (
                                          <span className="history-sent-info">
                                            Wysłano ({sentAtFormatted}) przez: {latestPaToTt.sentBy} {latestPaToTt.sentByLastName || ''}
                                          </span>
                                        );
                                      } else if (latestPaToTt) {
                                        return (
                                          <button
                                            className="history-send-btn"
                                            onClick={async () => {
                                              try {
                                                const now = new Date();
                                                const dateStr = now.toLocaleDateString('pl-PL');
                                                const timeStr = now.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
                                                const sentAt = `${dateStr} ${timeStr}`;
                                                await dashboardHistoryAPI.markAsSent(
                                                  latestPaToTt.id,
                                                  user?.name || user?.firstName || 'Nieznany',
                                                  user?.lastName || '',
                                                  sentAt
                                                );
                                                // Odśwież historię
                                                const project = getCurrentProject();
                                                if (project) {
                                                  const updatedHistory = await dashboardHistoryAPI.getByProject(project);
                                                  setDashboardHistory(updatedHistory);
                                                }
                                              } catch (error) {
                                                console.error('Błąd oznaczania jako wysłane:', error);
                                                alert('Nie udało się oznaczyć jako wysłane');
                                              }
                                            }}
                                          >
                                            Wyślij
                                          </button>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                /* Karta czyszczenia historii */
                <div className="history-clear-section">
                  <h4>Usuń wpisy z historii</h4>
                  
                  {/* Komunikat sukcesu/błędu */}
                  {historyClearMessage && (
                    <div className={`history-clear-message ${historyClearMessage.type === 'success' ? 'history-clear-message-success' : 'history-clear-message-error'}`}>
                      <div className="history-clear-message-icon">
                        {historyClearMessage.type === 'success' ? '✅' : '❌'}
                      </div>
                      <div className="history-clear-message-content">
                        <div className="history-clear-message-text">
                          {historyClearMessage.message}
                        </div>
                      </div>
                      <button
                        className="history-clear-message-close"
                        onClick={() => setHistoryClearMessage(null)}
                        aria-label="Zamknij"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  
                  {dashboardHistory.length === 0 ? (
                    <p className="no-history">Brak wpisów do usunięcia</p>
                  ) : (
                    <>
                      <div className="history-delete-list">
                        {dashboardHistory.map(entry => (
                          <div key={entry.id} className="history-delete-item">
                            <label className="history-delete-checkbox-label">
                              <input
                                type="checkbox"
                                checked={selectedHistoryEntries.includes(entry.id)}
                                onChange={() => {
                                  if (selectedHistoryEntries.includes(entry.id)) {
                                    setSelectedHistoryEntries(selectedHistoryEntries.filter(id => id !== entry.id));
                                  } else {
                                    setSelectedHistoryEntries([...selectedHistoryEntries, entry.id]);
                                  }
                                }}
                              />
                              <div className="history-delete-item-content">
                                <div className="history-delete-item-title">
                                  {entry.cardNumber} - {entry.statusFrom} → {entry.statusTo}
                                </div>
                                <div className="history-delete-item-details">
                                  <span className="history-badge">
                                    {entry.date} {entry.time}
                                    {entry.statusToDate && entry.statusTo === 'TT' && (
                                      <span> → {entry.statusToDate} {entry.statusToTime}</span>
                                    )}
                                  </span>
                                  <span className="history-badge" style={{ marginLeft: '8px' }}>
                                    Data: {entry.dateValue || 'Brak'} | Czas: {entry.timeValue || 'Brak'} | Trailer: {entry.trailerId || 'Brak'}
                                  </span>
                                  <span className="history-badge" style={{ marginLeft: '8px' }}>
                                    przez: {entry.userName} {entry.userLastName || ''}
                                  </span>
                                </div>
                              </div>
                            </label>
                          </div>
                        ))}
                      </div>
                      <div className="history-delete-actions">
                        <button
                          className="history-delete-btn"
                          onClick={async () => {
                            // Sprawdź uprawnienie do usuwania historii
                            if (!hasDashboardPermission(user, 'deleteHistory')) {
                              alert('Nie masz uprawnień do usuwania historii.');
                              return;
                            }
                            
                            if (selectedHistoryEntries.length === 0) return;
                            
                            // Pokaż potwierdzenie w modalu zamiast window.confirm
                            const confirmed = window.confirm(`Czy na pewno chcesz usunąć ${selectedHistoryEntries.length} wpis(ów) z historii?`);
                            if (!confirmed) return;
                            
                            try {
                              const project = getCurrentProject();
                              const countToDelete = selectedHistoryEntries.length;
                              
                              if (selectedHistoryEntries.length === 1) {
                                await dashboardHistoryAPI.delete(selectedHistoryEntries[0]);
                              } else {
                                await dashboardHistoryAPI.deleteMany(selectedHistoryEntries);
                              }
                              
                              // Odśwież listę historii
                              const updatedHistory = await dashboardHistoryAPI.getByProject(project);
                              setDashboardHistory(updatedHistory || []);
                              setSelectedHistoryEntries([]);
                              
                              // Pokaż komunikat sukcesu w modalu
                              setHistoryClearMessage({
                                type: 'success',
                                message: `Usunięto ${countToDelete} wpis(ów) z historii`
                              });
                              
                              // Automatycznie ukryj komunikat po 5 sekundach
                              setTimeout(() => {
                                setHistoryClearMessage(null);
                              }, 5000);
                            } catch (error) {
                              console.error('Błąd usuwania wpisów z historii:', error);
                              
                              // Pokaż komunikat błędu w modalu
                              setHistoryClearMessage({
                                type: 'error',
                                message: `Nie udało się usunąć wpisów: ${error.message || error}`
                              });
                              
                              // Automatycznie ukryj komunikat po 7 sekundach
                              setTimeout(() => {
                                setHistoryClearMessage(null);
                              }, 7000);
                            }
                          }}
                          disabled={selectedHistoryEntries.length === 0}
                        >
                          Usuń zaznaczone ({selectedHistoryEntries.length})
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Przypomnienie */}
      {showReminderModal && (
        <div className="modal-overlay" onClick={() => setShowReminderModal(false)}>
          <div className="modal-content reminder-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Przypomnienie</h3>
              <button
                className="modal-close-btn"
                onClick={() => {
                  setShowReminderModal(false);
                  setReminderModalTab('add');
                  setSelectedRemindersToDelete([]);
                  setReminderSuccessMessage(null);
                }}
              >
                ×
              </button>
            </div>
            
            {/* Karty/Zakładki */}
            <div className="reminder-modal-tabs">
              <button
                className={`reminder-tab ${reminderModalTab === 'add' ? 'active' : ''}`}
                onClick={() => {
                  setReminderModalTab('add');
                  setSelectedRemindersToDelete([]);
                }}
              >
                Dodaj nowe przypomnienie
              </button>
              <button
                className={`reminder-tab ${reminderModalTab === 'delete' ? 'active' : ''}`}
                onClick={() => {
                  setReminderModalTab('delete');
                  setSelectedRemindersToDelete([]);
                }}
              >
                Usuń przypomnienie
              </button>
            </div>

            <div className="modal-body">
              {reminderModalTab === 'add' ? (
                /* Formularz dodawania przypomnienia */
                <div className="reminder-form">
                  <h4>Dodaj nowe przypomnienie</h4>
                  
                  {/* Komunikat sukcesu */}
                  {reminderSuccessMessage && (
                    <div className="reminder-success-message">
                      <div className="reminder-success-icon">✅</div>
                      <div className="reminder-success-content">
                        <div className="reminder-success-title">
                          Przypomnienie "{reminderSuccessMessage.title}" zostało dodane!
                        </div>
                        <div className="reminder-success-info">
                          Typ: {reminderSuccessMessage.info}
                        </div>
                        <div className="reminder-success-info">
                          Wyślij do: {reminderSuccessMessage.assignedTo}
                        </div>
                      </div>
                      <button
                        className="reminder-success-close"
                        onClick={() => setReminderSuccessMessage(null)}
                        aria-label="Zamknij"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  
                <div className="reminder-form-row">
                  <label className="reminder-label">Tytuł:</label>
                  <input
                    type="text"
                    className="reminder-input"
                    value={reminderForm.title}
                    onChange={(e) => setReminderForm({...reminderForm, title: e.target.value})}
                    placeholder="Wprowadź tytuł przypomnienia"
                    disabled={!hasDashboardPermission(user, 'addReminders')}
                  />
                </div>
                <div className="reminder-form-row">
                  <label className="reminder-label">Opis:</label>
                  <textarea
                    className="reminder-textarea"
                    value={reminderForm.description}
                    onChange={(e) => setReminderForm({...reminderForm, description: e.target.value})}
                    placeholder="Wprowadź opis przypomnienia (opcjonalnie)"
                    rows={3}
                    disabled={!hasDashboardPermission(user, 'addReminders')}
                  />
                </div>
                <div className="reminder-form-row">
                  <label className="reminder-label">Wyślij do:</label>
                  <select
                    className="reminder-select"
                    value={reminderForm.assignedTo}
                    onChange={(e) => setReminderForm({...reminderForm, assignedTo: e.target.value})}
                    disabled={!hasDashboardPermission(user, 'addReminders')}
                  >
                    <option value="all">Wszyscy użytkownicy</option>
                    {(() => {
                      // Filtruj użytkowników: wyklucz pracowników (role === 'employee')
                      // Upewnij się, że users jest tablicą
                      const usersList = Array.isArray(users) ? users : [];
                      const filteredUsers = usersList.filter(u => {
                        // Sprawdź czy użytkownik istnieje i ma rolę różną od 'employee'
                        return u && u.id && u.role && u.role !== 'employee';
                      });
                      
                      // Debug: loguj tylko w trybie deweloperskim
                      if (process.env.NODE_ENV === 'development' && filteredUsers.length === 0 && usersList.length > 0) {
                        console.log('⚠️ Brak użytkowników do wyświetlenia w przypomnieniach:', {
                          totalUsers: usersList.length,
                          filteredUsers: filteredUsers.length,
                          usersWithEmployeeRole: usersList.filter(u => u && u.role === 'employee').length,
                          allRoles: usersList.map(u => u?.role).filter(Boolean)
                        });
                      }
                      
                      return filteredUsers.map(u => {
                        // Wyświetl imię i nazwisko, lub email jeśli brak imienia/nazwiska
                        const displayName = u.name || (u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email) || u.email || 'Brak nazwy';
                        return (
                          <option key={u.id} value={u.id}>
                            {displayName}
                          </option>
                        );
                      });
                    })()}
                  </select>
                </div>
                <div className="reminder-form-row">
                  <label className="reminder-checkbox-label">
                    <input
                      type="checkbox"
                      checked={reminderForm.isRecurring}
                      onChange={(e) => setReminderForm({...reminderForm, isRecurring: e.target.checked})}
                      disabled={!hasDashboardPermission(user, 'addReminders')}
                    />
                    <span>Przypomnienie powtarzające się</span>
                  </label>
                </div>
                {reminderForm.isRecurring ? (
                  <>
                    <div className="reminder-form-row">
                      <label className="reminder-label">Dni tygodnia:</label>
                      <div className="reminder-days-checkboxes">
                        {[
                          { value: 0, label: 'Niedziela' },
                          { value: 1, label: 'Poniedziałek' },
                          { value: 2, label: 'Wtorek' },
                          { value: 3, label: 'Środa' },
                          { value: 4, label: 'Czwartek' },
                          { value: 5, label: 'Piątek' },
                          { value: 6, label: 'Sobota' }
                        ].map(day => (
                          <label key={day.value} className="reminder-day-checkbox-label">
                            <input
                              type="checkbox"
                              checked={reminderForm.recurringDays.includes(day.value)}
                              onChange={(e) => {
                                if (!hasDashboardPermission(user, 'addReminders')) return;
                                if (e.target.checked) {
                                  setReminderForm({
                                    ...reminderForm,
                                    recurringDays: [...reminderForm.recurringDays, day.value]
                                  });
                                } else {
                                  setReminderForm({
                                    ...reminderForm,
                                    recurringDays: reminderForm.recurringDays.filter(d => d !== day.value)
                                  });
                                }
                              }}
                              disabled={!hasDashboardPermission(user, 'addReminders')}
                            />
                            <span>{day.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="reminder-form-row">
                      <label className="reminder-label">Godzina:</label>
                      <input
                        type="time"
                        className="reminder-input"
                        value={reminderForm.recurringTime}
                        onChange={(e) => setReminderForm({...reminderForm, recurringTime: e.target.value})}
                        disabled={!hasDashboardPermission(user, 'addReminders')}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="reminder-form-row reminder-date-time-row">
                      <div className="reminder-form-field">
                        <label className="reminder-label">Data:</label>
                        <input
                          type="date"
                          className="reminder-input"
                          value={reminderForm.date}
                          onChange={(e) => setReminderForm({...reminderForm, date: e.target.value})}
                        />
                      </div>
                      <div className="reminder-form-field">
                        <label className="reminder-label">Godzina:</label>
                        <input
                          type="time"
                          className="reminder-input"
                          value={reminderForm.time}
                          onChange={(e) => setReminderForm({...reminderForm, time: e.target.value})}
                          disabled={!hasDashboardPermission(user, 'addReminders')}
                        />
                      </div>
                    </div>
                  </>
                )}
                <button
                  className="reminder-add-btn"
                  onClick={handleAddReminder}
                  disabled={!hasDashboardPermission(user, 'addReminders')}
                >
                  Dodaj przypomnienie
                </button>
              </div>
              ) : (
                /* Karta usuwania przypomnień */
                <div className="reminder-delete-section">
                  <h4>Usuń przypomnienie</h4>
                  {(() => {
                    // Filtruj przypomnienia - usuń starsze niż 14 dni
                    const now = new Date();
                    const fourteenDaysAgo = new Date(now);
                    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
                    
                    const filteredReminders = reminders.filter(reminder => {
                      if (reminder.isRecurring) {
                        // Dla przypomnień powtarzających się - sprawdź createdAt
                        const createdAt = reminder.createdAt ? new Date(reminder.createdAt) : null;
                        if (!createdAt) return true; // Jeśli brak daty utworzenia, pokaż (bezpieczeństwo)
                        return createdAt >= fourteenDaysAgo;
                      } else {
                        // Dla przypomnień jednorazowych - sprawdź datę przypomnienia
                        if (!reminder.date) return true; // Jeśli brak daty, pokaż (bezpieczeństwo)
                        const reminderDate = new Date(reminder.date);
                        return reminderDate >= fourteenDaysAgo;
                      }
                    });
                    
                    return filteredReminders.length === 0 ? (
                      <p className="no-reminders">Brak przypomnień do usunięcia</p>
                    ) : (
                      <>
                        <div className="reminders-delete-list">
                          {filteredReminders.map(reminder => (
                          <div key={reminder.id} className="reminder-delete-item">
                            <label className="reminder-delete-checkbox-label">
                              <input
                                type="checkbox"
                                checked={selectedRemindersToDelete.includes(reminder.id)}
                                onChange={() => handleToggleReminderSelection(reminder.id)}
                              />
                              <div className="reminder-delete-item-content">
                                <div className="reminder-delete-item-title">{reminder.title}</div>
                                {reminder.description && (
                                  <div className="reminder-delete-item-description">{reminder.description}</div>
                                )}
                                <div className="reminder-delete-item-details">
                                  {reminder.isRecurring ? (
                                    <span className="reminder-badge recurring">
                                      Powtarzające się: {(() => {
                                        const days = reminder.recurringDays || (reminder.recurringDay !== undefined ? [parseInt(reminder.recurringDay)] : []);
                                        const dayNames = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];
                                        const selectedDays = days.map(d => dayNames[d]).join(', ');
                                        return `${selectedDays} o ${reminder.recurringTime}`;
                                      })()}
                                    </span>
                                  ) : (
                                    <span className="reminder-badge one-time">
                                      Jednorazowe: {reminder.date} o {reminder.time}
                                    </span>
                                  )}
                                  <span className="reminder-badge assigned-to" style={{ marginLeft: '8px', backgroundColor: '#e3f2fd', color: '#1565c0', borderColor: '#2196F3' }}>
                                    {reminder.assignedTo ? (() => {
                                      const assignedUser = users.find(u => u.id === reminder.assignedTo);
                                      return assignedUser ? `Do: ${assignedUser.name || assignedUser.email}` : `Do: Użytkownik #${reminder.assignedTo}`;
                                    })() : 'Do: Wszyscy'}
                                  </span>
                                </div>
                              </div>
                            </label>
                          </div>
                        ))}
                      </div>
                      <button
                        className="reminder-delete-selected-btn"
                        onClick={handleDeleteSelectedReminders}
                        disabled={selectedRemindersToDelete.length === 0}
                      >
                        Usuń zaznaczone ({selectedRemindersToDelete.length})
                      </button>
                    </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal potwierdzający zmianę statusu */}
      {showStatusConfirmModal && (
        <div className="modal-overlay" onClick={() => setShowStatusConfirmModal(false)}>
          <div className="modal-content confirm-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Potwierdzenie zmiany statusu</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowStatusConfirmModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="confirm-delete-content">
                <div className="confirm-delete-icon">⚠️</div>
                <div className="confirm-delete-message">
                  Czy na pewno chcesz zmienić status karty L{statusConfirmData.cardIndex !== null ? cardsData[statusConfirmData.cardIndex]?.cardNumber : ''} z {statusConfirmData.oldStatus} na {statusConfirmData.newStatus}?
                </div>
                {statusConfirmData.newStatus === 'TT' && statusConfirmData.oldStatus === 'PA' && (
                  <div className="confirm-delete-warning">
                    ⚠️ Uwaga: Po zmianie statusu na TT wszystkie komórki w karcie zostaną wyzerowane!
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="confirm-delete-cancel-btn"
                onClick={() => setShowStatusConfirmModal(false)}
              >
                Nie
              </button>
              <button
                className="confirm-delete-confirm-btn"
                onClick={() => {
                  if (statusConfirmData.callback) {
                    statusConfirmData.callback();
                  }
                  setShowStatusConfirmModal(false);
                }}
              >
                Tak
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal potwierdzenia usunięcia */}
      {showDeleteConfirmModal && (
        <div className="modal-overlay" onClick={handleCancelDelete}>
          <div className="modal-content confirm-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Potwierdzenie usunięcia</h3>
              <button
                className="modal-close-btn"
                onClick={handleCancelDelete}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="confirm-delete-content">
                <div className="confirm-delete-icon">⚠️</div>
                <div className="confirm-delete-message">
                  Czy na pewno chcesz usunąć {deleteConfirmData.count} {deleteConfirmData.count === 1 ? 'przypomnienie' : deleteConfirmData.count < 5 ? 'przypomnienia' : 'przypomnień'}?
                </div>
                <div className="confirm-delete-warning">
                  Ta operacja jest nieodwracalna.
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="confirm-delete-cancel-btn"
                onClick={handleCancelDelete}
              >
                Anuluj
              </button>
              <button
                className="confirm-delete-ok-btn"
                onClick={handleConfirmDelete}
              >
                Usuń
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup przypomnienia */}
      {activeReminderPopup && (
        <div className="reminder-popup-overlay" onClick={handleCloseReminderPopup}>
          <div className="reminder-popup" onClick={(e) => e.stopPropagation()}>
            <div className="reminder-popup-header">
              <div className="reminder-popup-icon">🔔</div>
              <h3>Przypomnienie</h3>
              <button
                className="reminder-popup-close"
                onClick={handleCloseReminderPopup}
              >
                ×
              </button>
            </div>
            <div className="reminder-popup-body">
              <div className="reminder-popup-title">{activeReminderPopup.title}</div>
              {activeReminderPopup.description && (
                <div className="reminder-popup-description">{activeReminderPopup.description}</div>
              )}
            </div>
            <div className="reminder-popup-footer">
              <div className="reminder-postpone-section">
                <label className="reminder-postpone-label">Odłóż o:</label>
                <select
                  className="reminder-postpone-select"
                  value={postponeMinutes}
                  onChange={(e) => setPostponeMinutes(parseInt(e.target.value))}
                >
                  <option value="5">5 minut</option>
                  <option value="10">10 minut</option>
                  <option value="15">15 minut</option>
                  <option value="30">30 minut</option>
                  <option value="60">1 godzinę</option>
                  <option value="120">2 godziny</option>
                </select>
                <button
                  className="reminder-popup-postpone-btn"
                  onClick={handlePostponeReminder}
                >
                  Odłóż
                </button>
              </div>
              <button
                className="reminder-popup-confirm-btn"
                onClick={handleConfirmReminder}
              >
                Potwierdź
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup powiadomienia o alercie jakościowym */}
      {activeQualityAlertPopup && (
        <div className="reminder-popup-overlay" onClick={() => handleCloseQualityAlertPopup(true)}>
          <div className="reminder-popup" onClick={(e) => e.stopPropagation()}>
            <div className="reminder-popup-header">
              <div className="reminder-popup-icon">⚠️</div>
              <h3>Powiadomienie – alert jakości</h3>
              <button
                className="reminder-popup-close"
                onClick={() => handleCloseQualityAlertPopup(true)}
              >
                ×
              </button>
            </div>
            <div className="reminder-popup-body">
              <div className="reminder-popup-title">{activeQualityAlertPopup.title}</div>
              {activeQualityAlertPopup.description && (
                <div className="reminder-popup-description">{activeQualityAlertPopup.description}</div>
              )}
              {activeQualityAlertPopup.priority && (
                <div className="reminder-popup-description" style={{ marginTop: '8px' }}>
                  Priorytet: {activeQualityAlertPopup.priority}
                </div>
              )}
            </div>
            <div className="reminder-popup-footer">
              <button
                className="reminder-popup-confirm-btn"
                onClick={() => {
                  handleCloseQualityAlertPopup(true);
                  if (activeQualityAlertPopup.alertId != null) {
                    window.dispatchEvent(new CustomEvent('navigate', {
                      detail: { view: 'quality-tablica', openAlertId: activeQualityAlertPopup.alertId }
                    }));
                  }
                }}
              >
                Przejdź do karty
              </button>
              <button
                className="reminder-popup-postpone-btn"
                style={{ marginLeft: '8px' }}
                onClick={() => handleCloseQualityAlertPopup(true)}
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sekcja z tabelami danych przyczepy */}
      <div className="trailer-section">
        <div className="trailer-tables-container">
          {sortedCards.map((card, index) => {
            // Znajdź oryginalny indeks karty w cardsData dla zachowania funkcjonalności
            const originalIndex = cardsData.findIndex(c => c.cardNumber === card.cardNumber);
            return (
            <div key={`card-${card.cardNumber}`} className="trailer-table-wrapper">
              {/* Kompaktowy header karty */}
              <div className="card-header-compact">
                <div 
                  className={`card-number-large ${user?.role !== 'owner' ? 'disabled' : ''}`}
                  onClick={() => {
                    if (user?.role === 'owner') {
                      handleResetCard(originalIndex);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (user?.role === 'owner') {
                      e.currentTarget.classList.add('card-number-hover');
                    }
                  }}
                  onMouseLeave={(e) => e.currentTarget.classList.remove('card-number-hover')}
                  title={user?.role === 'owner' ? "Kliknij, aby wyzerować wszystkie pola w karcie" : ""}
                  style={{ cursor: user?.role === 'owner' ? 'pointer' : 'default' }}
                >
                  L{card.cardNumber}
                </div>
                <div className="card-fields-grid">
                  <div className="card-field-item">
                    <label className="card-field-label">DATA</label>
                    <input
                      type="date"
                      className={`card-field-input ${validationErrors[originalIndex]?.date ? 'validation-error' : ''}`}
                      value={card.date}
                      onChange={(e) => handleDateChange(originalIndex, e.target.value)}
                      disabled={!hasDashboardPermission(user, 'edit') || card.code === 'PA'}
                      onFocus={(e) => {
                        focusedInputsRef.current.add(e.target.id || `date-${originalIndex}`);
                        updateLastActivity();
                      }}
                      onBlur={(e) => {
                        focusedInputsRef.current.delete(e.target.id || `date-${originalIndex}`);
                      }}
                    />
                  </div>
                  <div className="card-field-item">
                    <label className="card-field-label">GODZINA</label>
                    <input
                      type="time"
                      className={`card-field-input ${validationErrors[originalIndex]?.time ? 'validation-error' : ''}`}
                      value={card.time || ''}
                      onChange={(e) => handleTimeChange(originalIndex, e.target.value)}
                      disabled={!hasDashboardPermission(user, 'edit') || card.code === 'PA'}
                      onFocus={(e) => {
                        focusedInputsRef.current.add(e.target.id || `time-${originalIndex}`);
                        updateLastActivity();
                      }}
                      onBlur={(e) => {
                        focusedInputsRef.current.delete(e.target.id || `time-${originalIndex}`);
                      }}
                    />
                  </div>
                  <div className="card-field-item trailer-field">
                    <label className="card-field-label">TRAILER</label>
                    <input
                      type="text"
                      className={`card-field-input ${validationErrors[originalIndex]?.trailerId ? 'validation-error' : ''}`}
                      value={card.trailerId || ''}
                      onChange={(e) => handleTrailerIdChange(originalIndex, e.target.value)}
                      placeholder="Trailer number"
                      disabled={!hasDashboardPermission(user, 'edit') || card.code === 'PA'}
                      onFocus={(e) => {
                        focusedInputsRef.current.add(e.target.id || `trailer-${originalIndex}`);
                        updateLastActivity();
                      }}
                      onBlur={(e) => {
                        focusedInputsRef.current.delete(e.target.id || `trailer-${originalIndex}`);
                      }}
                    />
                  </div>
                  <div className="card-field-item status-field">
                    <label className="card-field-label">STATUS</label>
                    <div className="status-select-wrapper-compact">
                      <select
                        className="card-field-select"
                        value={card.code || 'PL'}
                        onChange={(e) => handleStatusChange(originalIndex, e.target.value)}
                        disabled={!hasDashboardPermission(user, 'edit')}
                      >
                        <option value="PL">PL</option>
                        <option value="PA">PA</option>
                        <option value="TT">TT</option>
                      </select>
                      <div className="status-arrow">▼</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Pole "Aktualizacja o" z przełącznikiem */}
              <div className="last-update-section">
                <label className="last-update-label">Aktualizacja o</label>
                <div className="last-update-time">
                  {card.lastUpdateTime || '--:--'}
                </div>
                {hasDashboardPermission(user, 'viewExtendedColumns') && (
                  <div className="extended-columns-toggle">
                    <label className="toggle-switch-label">
                      <input
                        type="checkbox"
                        className="toggle-switch-input"
                        checked={showExtendedColumns[card.cardNumber] || false}
                        onChange={(e) => {
                          const newValue = e.target.checked;
                          setShowExtendedColumns(prev => {
                            const updated = { ...prev, [card.cardNumber]: newValue };
                            localStorage.setItem('dashboard_showExtendedColumns', JSON.stringify(updated));
                            return updated;
                          });
                        }}
                      />
                      <span className="toggle-switch-slider"></span>
                    </label>
                    <span className="toggle-switch-text">Przełącz</span>
                  </div>
                )}
              </div>
        
              <div className="trailer-table">
                <div className={`table-header-row ${(showExtendedColumns[card.cardNumber] || false) && hasDashboardPermission(user, 'viewExtendedColumns') ? 'extended-columns' : ''}`}>
                  <div className="table-header-cell">J</div>
                  <div className="table-header-cell">SEQ</div>
                  <div className="table-header-cell">ILOŚĆ</div>
                  <div className="table-header-cell" title="WYKONANE">WYK</div>
                  {(showExtendedColumns[card.cardNumber] || false) && hasDashboardPermission(user, 'viewExtendedColumns') ? (
                    <>
                      <div className="table-header-cell" title="DO WYKONANIA">OCZ</div>
                      <div className="table-header-cell" title="URGENT PARTS">UP</div>
                      <div className="table-header-cell" title="URGENT PARTS DOŁOŻONE">UPok</div>
                      <div className="table-header-cell" title="DOSTĘPNE DO UŁOŻENIA URGENT PARTS">AV</div>
                      <div className="table-header-cell" title="BRAK CZĘŚCI - POZYCJA WYSŁANA DO URGENT PARTS">SK</div>
                      <div className="table-header-cell" title="CZEKA NA WYDANIE Z MAGAZYNU">ER</div>
                    </>
                  ) : (
                    <>
                      <div className="table-header-cell">PRZYPISANIE</div>
                      <div className="table-header-cell">KOMENTARZ</div>
                    </>
                  )}
                </div>
                
                {card.data && card.data.length > 0 ? card.data.map((row, rowIndex) => {
                  // Sprawdź status wiersza dla podświetlenia
                  const doZrobienia = row.doZrobienia !== null && row.doZrobienia !== undefined ? parseInt(row.doZrobienia) : null;
                  const zrobione = row.zrobione !== null && row.zrobione !== undefined ? parseInt(row.zrobione) : null;
                  
                  let rowClass = '';
                  if (doZrobienia !== null && !Number.isNaN(doZrobienia) && doZrobienia > 0) {
                    if (zrobione !== null && zrobione === doZrobienia) {
                      rowClass = 'row-completed'; // Zielony - zadanie wykonane
                    } else {
                      rowClass = 'row-in-progress'; // Pomarańczowy - zadanie do zrobienia
                    }
                  }
                  
                  const hasIloscForCheckJColor =
                    doZrobienia !== null && !Number.isNaN(doZrobienia) && doZrobienia > 0;
                  const checkVal = normalizeCheckField(row.check);
                  let jCellCheckClass = '';
                  if (hasIloscForCheckJColor && checkVal) {
                    if (checkVal === '1') jCellCheckClass = 'j-cell-check-1';
                    else if (checkVal === '2') jCellCheckClass = 'j-cell-check-2';
                    else if (checkVal === '3') jCellCheckClass = 'j-cell-check-3';
                  }
                  
                  return (
                    <div key={`${card.cardNumber}-${rowIndex}-${row.seq}`} className={`table-data-row ${rowClass} ${(showExtendedColumns[card.cardNumber] || false) && hasDashboardPermission(user, 'viewExtendedColumns') ? 'extended-columns' : ''}`}>
                      <div className={`table-data-cell j-cell ${jCellCheckClass}`}>
                        {row.j || ''}
                      </div>
                      <div 
                        className="table-data-cell seq-cell seq-cell-with-tooltip"
                        data-tooltip={row.opis && row.opis.trim() ? row.opis : ''}
                        title={row.opis && row.opis.trim() ? row.opis : ''}
                      >
                        {row.seq}
                      </div>
                      <div className="table-data-cell">
                        <input
                          type="number"
                          id={`input-${originalIndex}-${rowIndex}-doZrobienia`}
                          className={`table-input ${validationErrors[originalIndex]?.quantity ? 'validation-error' : ''}`}
                          value={row.doZrobienia || ''}
                          onChange={(e) => handleDataChange(originalIndex, rowIndex, 'doZrobienia', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, originalIndex, rowIndex, 'doZrobienia')}
                          disabled={!hasDashboardPermission(user, 'edit') || !isCardBasicFieldsFilled(card)}
                          onFocus={(e) => {
                            focusedInputsRef.current.add(e.target.id);
                            updateLastActivity();
                          }}
                          onBlur={(e) => {
                            focusedInputsRef.current.delete(e.target.id);
                          }}
                          onPaste={(e) => handlePaste(e, rowIndex, 'doZrobienia', selectedCell, originalIndex)}
                          min="0"
                          placeholder=""
                        />
                      </div>
                      <div className="table-data-cell">
                        <input
                          type="number"
                          id={`input-${originalIndex}-${rowIndex}-zrobione`}
                          className="table-input"
                          value={row.zrobione !== null && row.zrobione !== undefined ? row.zrobione : ''}
                          onChange={(e) => handleDataChange(originalIndex, rowIndex, 'zrobione', e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, originalIndex, rowIndex, 'zrobione')}
                          disabled={!hasDashboardPermission(user, 'edit') || !isCardBasicFieldsFilled(card)}
                          onFocus={(e) => {
                            focusedInputsRef.current.add(e.target.id);
                            updateLastActivity();
                          }}
                          onBlur={(e) => {
                            focusedInputsRef.current.delete(e.target.id);
                          }}
                          onPaste={(e) => handlePaste(e, rowIndex, 'zrobione', selectedCell, originalIndex)}
                          min="0"
                          placeholder=""
                        />
                      </div>
                      {(showExtendedColumns[card.cardNumber] || false) && hasDashboardPermission(user, 'viewExtendedColumns') ? (
                        <>
                          <div className="table-data-cell">
                            <input
                              type="text"
                              id={`input-${originalIndex}-${rowIndex}-ocz`}
                              className="table-input"
                              value={row.ocz || ''}
                              onChange={(e) => handleDataChange(originalIndex, rowIndex, 'ocz', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, originalIndex, rowIndex, 'ocz')}
                              disabled={!hasDashboardPermission(user, 'editExtendedColumns') || !isCardBasicFieldsFilled(card)}
                              onFocus={(e) => {
                                focusedInputsRef.current.add(e.target.id);
                                updateLastActivity();
                              }}
                              onBlur={(e) => {
                                focusedInputsRef.current.delete(e.target.id);
                              }}
                              onPaste={(e) => handlePaste(e, rowIndex, 'ocz', selectedCell, originalIndex)}
                              placeholder=""
                            />
                          </div>
                          <div className="table-data-cell">
                            <input
                              type="text"
                              id={`input-${originalIndex}-${rowIndex}-up`}
                              className="table-input"
                              value={row.up || ''}
                              onChange={(e) => handleDataChange(originalIndex, rowIndex, 'up', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, originalIndex, rowIndex, 'up')}
                              disabled={!hasDashboardPermission(user, 'editExtendedColumns') || !isCardBasicFieldsFilled(card)}
                              onFocus={(e) => {
                                focusedInputsRef.current.add(e.target.id);
                                updateLastActivity();
                              }}
                              onBlur={(e) => {
                                focusedInputsRef.current.delete(e.target.id);
                              }}
                              onPaste={(e) => handlePaste(e, rowIndex, 'up', selectedCell, originalIndex)}
                              placeholder=""
                            />
                          </div>
                          <div className="table-data-cell">
                            <input
                              type="text"
                              id={`input-${originalIndex}-${rowIndex}-upok`}
                              className="table-input"
                              value={row.upok || ''}
                              onChange={(e) => handleDataChange(originalIndex, rowIndex, 'upok', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, originalIndex, rowIndex, 'upok')}
                              disabled={!hasDashboardPermission(user, 'editExtendedColumns') || !isCardBasicFieldsFilled(card)}
                              onFocus={(e) => {
                                focusedInputsRef.current.add(e.target.id);
                                updateLastActivity();
                              }}
                              onBlur={(e) => {
                                focusedInputsRef.current.delete(e.target.id);
                              }}
                              onPaste={(e) => handlePaste(e, rowIndex, 'upok', selectedCell, originalIndex)}
                              placeholder=""
                            />
                          </div>
                          <div className="table-data-cell">
                            <input
                              type="text"
                              id={`input-${originalIndex}-${rowIndex}-av`}
                              className="table-input"
                              value={row.av || ''}
                              onChange={(e) => handleDataChange(originalIndex, rowIndex, 'av', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, originalIndex, rowIndex, 'av')}
                              disabled={!hasDashboardPermission(user, 'editExtendedColumns') || !isCardBasicFieldsFilled(card)}
                              onFocus={(e) => {
                                focusedInputsRef.current.add(e.target.id);
                                updateLastActivity();
                              }}
                              onBlur={(e) => {
                                focusedInputsRef.current.delete(e.target.id);
                              }}
                              onPaste={(e) => handlePaste(e, rowIndex, 'av', selectedCell, originalIndex)}
                              placeholder=""
                            />
                          </div>
                          <div className="table-data-cell">
                            <input
                              type="text"
                              id={`input-${originalIndex}-${rowIndex}-sk`}
                              className="table-input"
                              value={row.sk || ''}
                              onChange={(e) => handleDataChange(originalIndex, rowIndex, 'sk', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, originalIndex, rowIndex, 'sk')}
                              disabled={!hasDashboardPermission(user, 'editExtendedColumns') || !isCardBasicFieldsFilled(card)}
                              onFocus={(e) => {
                                focusedInputsRef.current.add(e.target.id);
                                updateLastActivity();
                              }}
                              onBlur={(e) => {
                                focusedInputsRef.current.delete(e.target.id);
                              }}
                              onPaste={(e) => handlePaste(e, rowIndex, 'sk', selectedCell, originalIndex)}
                              placeholder=""
                            />
                          </div>
                          <div className="table-data-cell">
                            <input
                              type="text"
                              id={`input-${originalIndex}-${rowIndex}-er`}
                              className="table-input"
                              value={row.er || ''}
                              onChange={(e) => handleDataChange(originalIndex, rowIndex, 'er', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, originalIndex, rowIndex, 'er')}
                              disabled={!hasDashboardPermission(user, 'editExtendedColumns') || !isCardBasicFieldsFilled(card)}
                              onFocus={(e) => {
                                focusedInputsRef.current.add(e.target.id);
                                updateLastActivity();
                              }}
                              onBlur={(e) => {
                                focusedInputsRef.current.delete(e.target.id);
                              }}
                              onPaste={(e) => handlePaste(e, rowIndex, 'er', selectedCell, originalIndex)}
                              placeholder=""
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div 
                            className={`table-data-cell assignment-cell ${!hasDashboardPermission(user, 'edit') || !isCardBasicFieldsFilled(card) ? 'disabled' : ''}`}
                            onDragOver={hasDashboardPermission(user, 'edit') && isCardBasicFieldsFilled(card) ? handleDragOver : undefined}
                            onDragLeave={hasDashboardPermission(user, 'edit') && isCardBasicFieldsFilled(card) ? handleDragLeave : undefined}
                            onDrop={hasDashboardPermission(user, 'edit') && isCardBasicFieldsFilled(card) ? (e) => handleDrop(e, originalIndex, rowIndex) : undefined}
                          >
                            {row.assignedTo ? (
                              <div 
                                className="assigned-user-badge"
                                draggable={hasDashboardPermission(user, 'edit') && isCardBasicFieldsFilled(card)}
                                onDragStart={(e) => {
                                  if (hasDashboardPermission(user, 'edit') && isCardBasicFieldsFilled(card)) {
                                    handleAssignedUserDragStart(e, row.assignedTo, originalIndex, rowIndex);
                                  } else {
                                    e.preventDefault();
                                  }
                                }}
                                onDragEnd={handleDragEnd}
                                style={{ 
                                  cursor: hasDashboardPermission(user, 'edit') && isCardBasicFieldsFilled(card) ? 'grab' : 'not-allowed',
                                  ...getShiftColor(getUserShift(row.assignedTo))
                                }}
                                title={hasDashboardPermission(user, 'edit') && isCardBasicFieldsFilled(card) ? "Przeciągnij, aby przenieść przypisanie" : "Wypełnij Data, Godzina i Trailer, aby edytować"}
                              >
                                {getFirstNameOnly(row.assignedTo)}
                                <button
                                  className="remove-assignment-btn"
                                  onClick={(e) => {
                                    if (hasDashboardPermission(user, 'edit') && isCardBasicFieldsFilled(card)) {
                                      e.stopPropagation();
                                      handleDataChange(originalIndex, rowIndex, 'assignedTo', null);
                                    } else {
                                      e.preventDefault();
                                    }
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()} // Zapobiegaj rozpoczęciu drag przy kliknięciu w przycisk
                                  title={hasDashboardPermission(user, 'edit') && isCardBasicFieldsFilled(card) ? "Usuń przypisanie" : "Wypełnij Data, Godzina i Trailer, aby edytować"}
                                  disabled={!hasDashboardPermission(user, 'edit') || !isCardBasicFieldsFilled(card)}
                                >
                                  ×
                                </button>
                              </div>
                            ) : (
                              <div className="assignment-drop-zone">
                                Przeciągnij tutaj
                              </div>
                            )}
                          </div>
                          <div
                            className="table-data-cell comment-cell"
                            title={parseCommentMeta(row.komentarz || '').text?.trim() || ''}
                          >
                            {(() => {
                              const parsed = parseCommentMeta(row.komentarz || '');
                              const commentText = parsed.text || '';
                              const commentUsers = parsed.users || [];
                              return (
                                <>
                                  {commentUsers.length > 0 && (
                                    <div className="comment-assigned-container">
                                      {commentUsers.map((userName) => (
                                        <div
                                          key={userName}
                                          className="comment-assigned-badge"
                                          style={{
                                            ...getShiftColor(getUserShift(userName))
                                          }}
                                          title={userName}
                                          draggable={hasDashboardPermission(user, 'edit') && isCardBasicFieldsFilled(card)}
                                          onDragStart={(e) => {
                                            if (hasDashboardPermission(user, 'edit') && isCardBasicFieldsFilled(card)) {
                                              handleCommentUserDragStart(e, userName, originalIndex, rowIndex);
                                            } else {
                                              e.preventDefault();
                                            }
                                          }}
                                          onDragEnd={handleDragEnd}
                                        >
                                          {getFirstNameOnly(userName)}
                                          <button
                                            className="comment-remove-assignment-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (!hasDashboardPermission(user, 'edit') || !isCardBasicFieldsFilled(card)) {
                                                return;
                                              }
                                              const updated = removeUserFromComment(row.komentarz || '', userName);
                                              handleDataChange(originalIndex, rowIndex, 'komentarz', updated);
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            title={hasDashboardPermission(user, 'edit') && isCardBasicFieldsFilled(card) ? "Usuń przypisanie" : "Wypełnij Data, Godzina i Trailer, aby edytować"}
                                            disabled={!hasDashboardPermission(user, 'edit') || !isCardBasicFieldsFilled(card)}
                                          >
                                            ×
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                            <textarea
                              id={`input-${originalIndex}-${rowIndex}-komentarz`}
                              className={`table-input table-input-comment ${commentText.trim() ? 'has-comment' : ''} ${commentUsers.length > 0 ? 'with-badge' : ''}`}
                              value={commentText}
                              onChange={(e) => handleDataChange(originalIndex, rowIndex, 'komentarz', e.target.value)}
                              onDragOver={(e) => {
                                if (hasDashboardPermission(user, 'edit') && isCardBasicFieldsFilled(card)) {
                                  handleDragOver(e);
                                }
                              }}
                              onDragLeave={(e) => {
                                if (hasDashboardPermission(user, 'edit') && isCardBasicFieldsFilled(card)) {
                                  handleDragLeave(e);
                                }
                              }}
                              onDrop={(e) => handleCommentDrop(e, originalIndex, rowIndex)}
                              disabled={!hasDashboardPermission(user, 'edit') || !isCardBasicFieldsFilled(card)}
                              placeholder="Wpisz komentarz..."
                              rows="2"
                              title={commentText.trim() ? commentText : ''}
                            />
                                </>
                              );
                            })()}
                          </div>
                        </>
                      )}
                    </div>
                  );
                }) : (
                  <div className="table-data-row">
                    <div className="table-data-cell" colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                      Brak danych
                    </div>
                  </div>
                )}
              </div>
            </div>
            );
          })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;





